import { Fragment, useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useTranslation, Trans } from "react-i18next";
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Collapse, Divider,
  FormControlLabel, Grid, IconButton, InputBase, LinearProgress, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import DeleteOutlineIcon   from "@mui/icons-material/DeleteOutline";
import Dialog              from "@plannel/components/Dialog";
import CategoryIcon        from "@mui/icons-material/Category";
import ExpandMoreIcon      from "@mui/icons-material/ExpandMore";
import ExpandLessIcon      from "@mui/icons-material/ExpandLess";
import OpenInNewIcon       from "@mui/icons-material/OpenInNew";
import AutoAwesomeIcon     from "@mui/icons-material/AutoAwesome";
import AttachFileIcon      from "@mui/icons-material/AttachFile";
import CheckIcon           from "@mui/icons-material/Check";
import CloseIcon           from "@mui/icons-material/Close";
import CampaignIcon        from "@mui/icons-material/Campaign";
import TrendingUpIcon      from "@mui/icons-material/TrendingUp";
import TrendingDownIcon    from "@mui/icons-material/TrendingDown";
import RemoveIcon          from "@mui/icons-material/Remove";
import WarningAmberIcon    from "@mui/icons-material/WarningAmber";
import CheckCircleIcon     from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon    from "@mui/icons-material/ErrorOutline";
import NewspaperIcon       from "@mui/icons-material/Newspaper";
import ForumIcon           from "@mui/icons-material/Forum";
import BarChartIcon        from "@mui/icons-material/BarChart";
import SmartToyIcon        from "@mui/icons-material/SmartToy";
import InventoryIcon       from "@mui/icons-material/Inventory";
import GavelIcon           from "@mui/icons-material/Gavel";
import StorefrontIcon      from "@mui/icons-material/Storefront";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import EditNoteIcon        from "@mui/icons-material/EditNote";
import SearchIcon          from "@mui/icons-material/Search";
import ShowChartIcon       from "@mui/icons-material/ShowChart";  // Google Trends
import LanguageIcon        from "@mui/icons-material/Language";   // AgentCore Browser
import TuneIcon            from "@mui/icons-material/Tune";       // 채널 설정
import DateRangeIcon       from "@mui/icons-material/DateRange";  // 수집 기간
import HistoryIcon         from "@mui/icons-material/History";     // 이전 기록
import RestoreIcon         from "@mui/icons-material/Restore";     // 기록 복원
import aiScenarioDemoService from "@plannel/services/ai/ai-scenario-demo-service";
import authService from "@plannel/services/system/auth-service";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import { ALL_INDUSTRY, COMMON_KEYWORDS, INDUSTRIES, industryKeywords, isKnownIndustry, keywordEntry }
  from "./marketingIndustryKeywords";
import { attributionKeys, channelByCode, eligibleChannels, isBuiltinAvailable } from "./marketingChannels";
import { toAgentLanguage } from "@plannel/utils/i18n";
import { updateHistoryState } from "@plannel/redux/modules/HistoryState";
import { FONT } from "@plannel/components/ai/typographyTokens";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import AiSegmentedControl from "./AiSegmentedControl";
import AiStyledSwitch from "./AiStyledSwitch";
import { fmtSignedMoney } from "./aiMoney";

// ── 색상 팔레트 ──────────────────────────────────────────────────────────────
// 시나리오 플래닝과 동일한 팔레트(블루 accent #185FA5). 기존 key 명은 유지하고 hex 만 정렬.
const C = {
  primary: "#185FA5", primaryBg: "#E6F1FB",
  success: "#1D9E75", successBg: "#E1F5EE",
  warning: "#BA7517", warningBg: "#FAEEDA",
  danger:  "#E24B4A", dangerBg:  "#FCEBEB",
  neutral: "#6B7280", neutralBg: "#F1F4F8",
  purple:  "#7c3aed", purpleBg:  "#f5f3ff",
  teal:    "#0891b2", tealBg:    "#e0f2fe",
  border:  "#E5E7EB", bg:        "#F8F9FB",
  text1:   "#111827", text2: "#4B5563", text3: "#9CA3AF",
};

// ── 신호 타입 아이콘 ──────────────────────────────────────────────────────
const SIG_ICON = {
  news:       <NewspaperIcon   sx={{ fontSize: 13 }} />,
  sns:        <ForumIcon       sx={{ fontSize: 13 }} />,
  sales:      <BarChartIcon    sx={{ fontSize: 13 }} />,
  forecast:   <SmartToyIcon    sx={{ fontSize: 13 }} />,
  internal:   <CampaignIcon    sx={{ fontSize: 13 }} />,
  history:    <BarChartIcon    sx={{ fontSize: 13 }} />,
  channel:    <StorefrontIcon  sx={{ fontSize: 13 }} />,
  supplier:   <InventoryIcon   sx={{ fontSize: 13 }} />,
  market:     <NewspaperIcon   sx={{ fontSize: 13 }} />,
  regulation: <GavelIcon       sx={{ fontSize: 13 }} />,
  contract:   <CheckCircleIcon sx={{ fontSize: 13 }} />,
  competitor: <StorefrontIcon  sx={{ fontSize: 13 }} />,
  inventory:  <InventoryIcon   sx={{ fontSize: 13 }} />,
  // 신규: Google Trends () + AgentCore Browser ()
  trend:      <ShowChartIcon   sx={{ fontSize: 13 }} />,
  web:        <LanguageIcon    sx={{ fontSize: 13 }} />,
};

// 소스명 기반 색상 힌트 (동일 소스끼리 구분)
const SIG_SOURCE_COLOR = {
  "Google Trends":             "#7c3aed",
  "Google Trends 연관검색":    "#7c3aed",
  "Google Trends (Browser)":   "#7c3aed",
  "뉴스 언급 빈도":            "#7c3aed",   // pytrends 429 fallback
  "AgentCore Browser":         "#0891b2",
  "AgentCore Browser (기사 본문)": "#0891b2",
  "웹 크롤링":                 "#64748b",
  "네이버 뉴스":               "#03c75a",
  "Brave Search":              "#fb923c",
};

// ── 수집 채널 메타 (개발용 표시) ──────────────────────────────────────────────
// labelKey 가 있으면 t(labelKey), 없으면 label 을 그대로 표시 (고유명사는 번역하지 않음)
const CHANNEL_META = {
  naver:   { label: "Naver Browser",                   color: "#03c75a", bg: "#f0fdf4" },
  google:  { labelKey: "mktIntelChannelGoogle",        color: "#ea4335", bg: "#fef2f2" },
  browser: { label: "Browser",                         color: "#0891b2", bg: "#e0f2fe" },
  http:    { label: "HTTP",                            color: "#94a3b8", bg: "#f1f5f9" },
  trends:  { label: "pytrends",                        color: "#7c3aed", bg: "#f5f3ff" },
  rss:     { label: "RSS",                             color: "#64748b", bg: "#f8fafc" },
  brave:   { label: "Brave",                           color: "#fb923c", bg: "#fff7ed" },
};
const channelMetaLabel = (chMeta, t) => (chMeta.labelKey ? t(chMeta.labelKey) : chMeta.label);

// 서버 오류 detail 은 "CODE: 상세" 형태의 영문 코드로 온다 — 코드를 그대로 그리지 않고
//   화면 문구(fallback)를 쓴다. 코드가 아닌 detail(구 응답)만 그대로 보여준다.
const apiErrorText = (e, fallback) => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string" && detail && !/^[A-Z][A-Z0-9_]{2,}(:|$)/.test(detail)) {
    return detail;
  }
  if (detail) console.warn("[marketing] api error", detail);
  return fallback;
};

// ── 수집 조건 행의 라벨 열 ───────────────────────────────────
// 라벨은 칩보다 약한 서식으로 두어 구분선 없이 칩 대역과 층을 나눈다.
// 행마다 컨트롤 높이가 달라도(세그먼트/칩/날짜) 라벨은 첫 줄 24px 밴드 안에서 가운데 정렬한다
const FIELD_LABEL_SX = { display: "flex", alignItems: "center", gap: 0.6, minHeight: 24 };
const FIELD_LABEL_ICON_SX = { fontSize: 14, color: C.neutral };
const FIELD_LABEL_TEXT_SX = { fontSize: 11, fontWeight: 600, color: C.neutral,
  letterSpacing: "0.02em", whiteSpace: "nowrap" };
// 칩 행(11px)에 높이를 맞춘 날짜 입력
const DP_SX = { width: 128,
  "& .MuiInputBase-root":  { fontSize: 11, bgcolor: "#fff" },
  "& .MuiInputBase-input": { p: "4px 8px" },
  "& .MuiIconButton-root": { p: "3px", mr: 0 },
  "& .MuiSvgIcon-root":    { fontSize: 15 } };
// 프리셋을 고른 상태 — 날짜는 계산 결과일 뿐이라 담색으로 잠근다.
// disabled 는 MUI 가 .Mui-disabled 로 덮어쓰므로 선택자를 맞춰야 한다.
const DP_SX_LOCKED = { ...DP_SX,
  "& .MuiInputBase-root": { fontSize: 11, bgcolor: C.neutralBg },
  "& .MuiInputBase-input.Mui-disabled": { p: "4px 8px", WebkitTextFillColor: C.text3 },
  "& .Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: C.border },
  "& .MuiIconButton-root": { p: "3px", mr: 0 },
  "& .MuiSvgIcon-root": { fontSize: 15, color: C.border } };

// ── 기사 수집 기간 ───────────────────────────────────────────────────────────
// days 는 백엔드(news_collector._PERIOD_PRESETS)가 아는 값만 보낸다.
const COLLECT_PERIODS = [
  { days: 7,   labelKey: "mktIntelPeriodWeek1" },
  { days: 30,  months: 1, labelKey: "mktIntelPeriodMonth1" },
  { days: 90,  months: 3, labelKey: "mktIntelPeriodMonth3" },
  { days: 180, months: 6, labelKey: "mktIntelPeriodMonth6" },
];
const DEFAULT_COLLECT_DAYS = 30;
const PERIOD_CUSTOM = "custom";   // 기간 세그먼트에서 프리셋이 아닌 직접 지정
// 수집 구간이 정본이고 프리셋 칩은 그 구간을 채우는 단축키다(백엔드도 구간만 본다).
const MAX_RANGE_DAYS = 1095;   // 3년 — 백엔드 resolve_window 와 같은 상한
const DAY_MS = 86400000;
const periodDateToStr = (dt) => {
  if (!(dt instanceof Date) || isNaN(dt.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
};
const periodStrToDate = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v || "");
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
};
const todayDate = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
const shiftDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
// 말일 보정 — 3월 31일의 한 달 전은 2월 31일이 아니라 2월 말일이다
const shiftMonths = (d, n) => {
  const head = new Date(d.getFullYear(), d.getMonth() - n, 1);
  const last = new Date(head.getFullYear(), head.getMonth() + 1, 0).getDate();
  return new Date(head.getFullYear(), head.getMonth(), Math.min(d.getDate(), last));
};
// 월 단위 칩은 달력으로 뺀다(1개월 = 한 달 전 같은 날). 주 단위만 일수로 뺀다.
const rangeFromPreset = (days) => {
  const end = todayDate();
  const opt = COLLECT_PERIODS.find(o => o.days === days);
  const start = opt?.months ? shiftMonths(end, opt.months) : shiftDays(end, -(days || 7));
  return { start: periodDateToStr(start), end: periodDateToStr(end) };
};
const rangeDays = (start, end) => {
  const s = periodStrToDate(start), e = periodStrToDate(end);
  return s && e ? Math.round((e - s) / DAY_MS) + 1 : 0;
};
const rangeChipLabel = (start, end) => `${start} ~ ${end}`;

// ── 판단 기록 자연어화 ─────────────────────────────────────────────────────
// 고정 step 코드는 번역 라벨로, LLM 이 자유 지정한 step(예: past_signal_check)은
// 코드 느낌이 없도록 정리해 표시. 영어 판정어(pass/demote/reject)도 번역어로.
const DECISION_STEP_KEY = {
  expand:   "mktIntelStepExpand",
  collect:  "mktIntelStepCollect",
  curate:   "mktIntelStepCurate",
  critique: "mktIntelStepCritique",
  impact:   "mktIntelStepImpact",
  past:     "mktIntelStepPast",
  final:    "mktIntelStepFinal",
};
const humanizeDecisionText = (s, t) =>
  (s || "")
    .replace(/\bpass\b/gi, t("mktIntelVerdictPass"))
    .replace(/\bdemote\b/gi, t("mktIntelVerdictDemote"))
    .replace(/\breject\b/gi, t("mktIntelVerdictReject"));
const decisionStepLabel = (step, t) => {
  if (!step) return "";
  if (DECISION_STEP_KEY[step]) return t(DECISION_STEP_KEY[step]);
  // LLM 이 붙인 코드형 step(4_past_signal_check 등) → 숫자·언더스코어 제거한 사람이 읽는 형태
  const cleaned = String(step).replace(/^\d+[_-]?/, "").replace(/[_-]+/g, " ").trim();
  return cleaned || t("mktIntelStepJudgment");
};

// 판단 로그 목록 — 코드가 남긴 집계와 AI 가 남긴 판단이 같은 단계 라벨로 섞여 나오므로
// 선두 마커로 저자를 구분한다(긴 AI 문장이 접힐 때 행 경계가 사라지지 않게 행잉 인덴트).
function AgentDecisionList({ decisions }) {
  const { t } = useTranslation();
  // 구 레코드에는 src 가 없다 — 마커를 아예 두지 않는다("AI 판단 없음"으로 오독되지 않게)
  const hasSrc = decisions.some((d) => d.src);
  return (
    <Box sx={{ bgcolor: "#fff", borderRadius: 1, p: "6px 10px", mt: 0.4 }}>
      {decisions.map((d, i) => (
        <Stack key={i} direction="row" spacing={0.7} alignItems="flex-start">
          <Box sx={{ minWidth: 12, flexShrink: 0, display: "flex",
            alignItems: "center", justifyContent: "center",
            height: `calc(${FONT.BODY} * 1.6)` }}>
            {hasSrc && d.src === "llm" ? (
              <AutoAwesomeIcon sx={{ fontSize: 11, color: C.primary }} />
            ) : (
              <Typography component="span"
                sx={{ fontSize: FONT.BODY, color: C.text3, lineHeight: 1 }}>·</Typography>
            )}
          </Box>
          <Typography sx={{ fontSize: FONT.BODY, color: C.text2, lineHeight: 1.6 }}>
            <b>{decisionStepLabel(d.step, t)}</b> — {humanizeDecisionText(d.reason || d.action, t)}
          </Typography>
        </Stack>
      ))}
    </Box>
  );
}

// ── 영향 평가 지표 임시 숨김 ───────────────────────────────────────────────
// 산출 근거를 설명할 수 있게 되면 false 로 되돌려 상시 노출한다
const HIDE_IMPACT_METRICS = true;

function MetricRevealToggle({ open, onToggle, sx }) {
  const { t } = useTranslation();
  return (
    // 옆 칩과 같은 토큰·라인박스로 맞춘다 — 하드코딩 px 는 폰트 확대/축소를 따라가지 못한다.
    // height 고정은 App.scss 의 1400px 이하 강제 padding 이 sx 를 눌러 높이가 늘어나기 때문.
    <Button size="small" onClick={onToggle}
      sx={{ fontSize: FONT.BODY, lineHeight: "normal", color: C.neutral,
        p: "1px 6px", minWidth: 0, height: 24, ...sx }}>
      {open ? t("collapse") : t("viewDetail")}
    </Button>
  );
}

// ── 리스크 배지 ───────────────────────────────────────────────────────────
// risk 코드값(HIGH/MEDIUM/LOW) → 번역 라벨 맵
const RISK_LABEL_KEY = {
  HIGH:   "riskLevelHighLabel",
  MEDIUM: "riskLevelMediumLabel",
  LOW:    "riskLevelLowLabel",
};
function RiskChip({ level }) {
  const { t } = useTranslation();
  const conf = {
    HIGH:   { bg: C.dangerBg,  color: C.danger,  Icon: ErrorOutlineIcon },
    MEDIUM: { bg: C.warningBg, color: C.warning, Icon: WarningAmberIcon },
    LOW:    { bg: C.successBg, color: C.success, Icon: CheckCircleIcon  },
  }[level] || { bg: C.neutralBg, color: C.neutral, Icon: CheckCircleIcon };
  const { Icon } = conf;
  return (
    <Chip size="small" label={RISK_LABEL_KEY[level] ? t(RISK_LABEL_KEY[level]) : level}
      icon={<Icon sx={{ fontSize: "13px !important" }} />}
      sx={{ bgcolor: conf.bg, color: conf.color, fontWeight: 700, fontSize: 10,
            border: `1px solid ${conf.color}30`,
            "& .MuiChip-icon": { color: conf.color } }} />
  );
}

// ── Live 수집 신호 행 (클릭으로 세부 내용 펼침) ──────────────────────────
function SignalReadRow({ signal }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const icon    = SIG_ICON[signal.type] || SIG_ICON.news;
  // 백엔드가 생성 출처명("뉴스 언급 빈도" 등)을 UI 언어로 번역해 보내므로,
  // 이름 매칭이 빗나가면 trend 타입/채널 기준으로 트렌드 색을 유지한다.
  const srcColor = SIG_SOURCE_COLOR[signal.source]
    || channelByCode(signal.channel_code)?.color
    || ((signal.type === "trend" || signal.channel === "trends") ? "#7c3aed" : C.neutral);
  const title   = signal.title   || signal.content?.slice(0, 80) || "";
  const content = signal.content || "";
  const hasMore = content.length > 0 || signal.url;
  // Google Trends / AgentCore Browser 신호는 배경 강조
  const isTrend   = signal.type === "trend";
  const isBrowser = signal.type === "web";
  const rowBg = isTrend ? `${C.purple}05` : isBrowser ? `${C.teal}05` : "transparent";
  // 수집 채널 메타 (개발용)
  const chMeta = CHANNEL_META[signal.channel] || null;

  return (
    <Box sx={{
      borderBottom: `1px solid ${C.border}`,
      "&:last-child": { borderBottom: "none" },
      bgcolor: rowBg,
      borderRadius: rowBg !== "transparent" ? 1 : 0,
      mb: rowBg !== "transparent" ? 0.3 : 0,
    }}>
      {/* 헤더 — 항상 클릭 가능 */}
      <Stack
        direction="row" spacing={1} alignItems="flex-start"
        onClick={() => hasMore && setOpen(o => !o)}
        sx={{
          py: 1, px: 0.5,
          cursor: hasMore ? "pointer" : "default",
          borderRadius: 1,
          "&:hover": hasMore ? { bgcolor: `${C.primary}08` } : {},
          userSelect: "none",
        }}
      >
        <Box sx={{ color: srcColor, flexShrink: 0, mt: "2px" }}>{icon}</Box>
        <Box flex={1} minWidth={0}>
          <Typography variant="caption"
            sx={{ fontSize: 10, fontWeight: 700, color: srcColor, display: "block", mb: 0.2 }}>
            {signal.source}
            {/* trend/web 전용 뱃지 */}
            {isTrend && (
              <Chip label="Trends" size="small"
                sx={{ ml: 0.5, height: 14, fontSize: 9, fontWeight: 700,
                  bgcolor: `${C.purple}15`, color: C.purple }} />
            )}
            {isBrowser && (
              <Chip label="Browser" size="small"
                sx={{ ml: 0.5, height: 14, fontSize: 9, fontWeight: 700,
                  bgcolor: `${C.teal}15`, color: C.teal }} />
            )}
            {/* trend_value 표시 */}
            {signal.trend_value > 0 && (
              <Chip label={t("mktIntelPopularity", { value: signal.trend_value })} size="small"
                sx={{ ml: 0.5, height: 14, fontSize: 9,
                  bgcolor: `${C.purple}10`, color: C.purple }} />
            )}
            {/* 개발용: 수집 채널 표시 */}
            {chMeta && (
              <Chip label={t("mktIntelViaChannel", { channel: channelMetaLabel(chMeta, t) })} size="small"
                sx={{ ml: 0.5, height: 14, fontSize: 9, fontWeight: 600,
                  bgcolor: chMeta.bg, color: chMeta.color,
                  border: `1px solid ${chMeta.color}30` }} />
            )}
          </Typography>
          <Typography variant="body2" fontSize={12.5} lineHeight={1.45}
            sx={{ overflow: "hidden", display: "-webkit-box",
                  WebkitLineClamp: open ? 999 : 2, WebkitBoxOrient: "vertical" }}>
            {title}
          </Typography>
        </Box>
        {hasMore && (
          <Box sx={{ color: C.neutral, flexShrink: 0, mt: "2px" }}>
            {open ? <ExpandLessIcon sx={{ fontSize: 15 }} /> : <ExpandMoreIcon sx={{ fontSize: 15 }} />}
          </Box>
        )}
      </Stack>

      {/* 세부 내용 — 펼쳤을 때 */}
      <Collapse in={open} timeout={150}>
        <Box sx={{ mx: 0.5, mb: 1, p: "8px 10px",
          bgcolor: `${C.primary}05`, borderRadius: 1,
          border: `1px solid ${C.primary}15` }}>
          {content ? (
            <Typography variant="body2" fontSize={12} lineHeight={1.7}
              color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
              {content}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.disabled">{t("mktIntelNoContent")}</Typography>
          )}
          <Stack direction="row" spacing={1.5} mt={0.8} alignItems="center">
            {signal.url && (
              <a href={signal.url} target="_blank" rel="noopener noreferrer"
                style={{ fontSize: 11, color: C.primary, display: "inline-flex",
                  alignItems: "center", gap: 3, textDecoration: "none" }}
                onClick={e => e.stopPropagation()}>
                <OpenInNewIcon sx={{ fontSize: 12 }} /> {t("mktIntelViewOriginal")}
              </a>
            )}
            {signal.pub_date && (
              <Typography variant="caption" color="text.disabled">
                {signal.pub_date?.slice(0, 25)}
              </Typography>
            )}
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
}

// ── 분석 결과의 신호 상세 행 ───────────────────────────────────────────────
function SignalRow({ signal }) {
  const { t } = useTranslation();
  const isUserInput = signal.type === "user_input";
  const icon = isUserInput
    ? <EditNoteIcon sx={{ fontSize: 13 }} />
    : (SIG_ICON[signal.type] || SIG_ICON.news);
  const rowColor = isUserInput ? C.purple : C.primary;
  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", py: 0.8,
      borderBottom: `1px solid ${C.border}`, "&:last-child": { borderBottom: "none" },
      ...(isUserInput && { bgcolor: `${C.purple}08`, borderRadius: 1, px: 0.8 }) }}>
      <Box sx={{ color: rowColor, flexShrink: 0, mt: "2px" }}>{icon}</Box>
      <Box flex={1}>
        <Typography variant="caption" fontWeight={700} color="text.secondary"
          sx={{ display: "block", fontSize: 10, color: isUserInput ? C.purple : undefined }}>
          {signal.source}
          {isUserInput && (
            <Chip label={t("mktIntelUserInputBadge")} size="small"
              sx={{ ml: 0.5, height: 14, fontSize: 9, fontWeight: 700,
                bgcolor: `${C.purple}15`, color: C.purple }} />
          )}
        </Typography>
        <Typography variant="body2" lineHeight={1.55} fontSize={12.5}
          sx={{ whiteSpace: "pre-wrap" }}>{signal.content}</Typography>
      </Box>
    </Box>
  );
}

// ── 코멘트 + 분석 버튼 섹션 ───────────────────────────────────────────────
const ATTACH_MAX_BYTES       = 10 * 1024 * 1024;       // 파일당 10MB — Jackson 문자열 한도(2천만 자) 안에 여유 있게
const ATTACH_MAX_TOTAL_BYTES = 60 * 1024 * 1024;       // 합계 60MB — base64 로 80MB. 클라우드 AgentCore 봉투 한도 100MB 기준
const ATTACH_EXTS      = ["pdf", "docx", "xlsx", "pptx"]; // AI 서버 attachments 모듈이 읽을 수 있는 형식 (서버 _READERS 와 같아야 한다)
const ATTACH_ACCEPT    = ATTACH_EXTS.map(x => `.${x}`).join(",");
const extOf = (name) => (name.includes(".") ? name.split(".").pop().toLowerCase() : "");
const b64Bytes = (b64) => Math.floor((b64?.length || 0) * 3 / 4);   // 이미 첨부된 파일의 원본 크기 근사 (base64 → bytes)
const fmtBytes = (n) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)}MB` : `${Math.max(1, Math.round(n / 1024))}KB`);
// 형식별 색 — 사무 문서 관례색(PDF 빨강·Word 파랑·Excel 초록·PowerPoint 주황)을 이 화면의 토큰에 대응
const ATTACH_TYPE_COLOR = { pdf: C.danger, docx: C.primary, xlsx: C.success, pptx: C.warning };
// OS 의 "동작 줄이기" 설정을 켠 사용자에게는 모든 전환·애니메이션을 끈다
const REDUCED_MOTION = "@media (prefers-reduced-motion: reduce)";

// 형식 아이콘의 접힌 모서리에 쓰는 연한 색 (ATTACH_TYPE_COLOR 와 짝)
const ATTACH_TYPE_BG    = { pdf: C.dangerBg, docx: C.primaryBg, xlsx: C.successBg, pptx: C.warningBg };
const ATTACH_WARNING_MS = 6000;   // 거절 경고가 스스로 닫히기까지
const ATTACH_EXIT_MS    = 200;    // 제거한 카드가 접히며 사라지는 시간
const ATTACH_CHECK_MS   = 1600;   // 다 읽은 카드에 체크가 떠 있는 시간
const ATTACH_SHAKE = { "0%,100%": { transform: "none" }, "20%,60%": { transform: "translateX(-4px)" }, "40%,80%": { transform: "translateX(4px)" } };

// FileReader 가 주는 "data:…;base64,XXXX" 에서 쉼표 뒤 base64 만 돌려준다 (서버는 접두 없는 base64 를 받는다).
//   onProgress(0~100): 읽는 동안 카드의 진행선과 "읽는 중 n%" 를 움직인다
//   onReader(reader):  읽는 중인 카드를 제거할 때 abort() 로 읽기를 멈출 수 있게 reader 를 건네준다
const readFileAsBase64 = (file, { onProgress, onReader } = {}) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onprogress = (e) => { if (e.lengthComputable && e.total > 0) onProgress?.(Math.round(e.loaded / e.total * 100)); };
  reader.onload  = () => resolve(String(reader.result).split(",")[1] || "");
  reader.onerror = () => reject(reader.error);
  reader.onabort = () => reject(new DOMException("aborted", "AbortError"));
  onReader?.(reader);
  reader.readAsDataURL(file);
});

// 형식 아이콘: 접힌 문서 윤곽 안에 내용 모양을 그려, 색을 구분하기 어려운 사용자도 모양으로 알아볼 수 있게 한다
//   (PDF=그림+문단, Word=문단, Excel=표, PowerPoint=슬라이드). 28×32 격자 기준
const ATTACH_DOC_PATH = "M6 2h11l7 7v19a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z";
function AttachFileTypeIcon({ ext, height = 30 }) {
  const color = ATTACH_TYPE_COLOR[ext] || C.neutral;
  const line  = { fill: "none", stroke: color, strokeWidth: 1.3, strokeLinecap: "round" };
  return (
    <svg width={Math.round(height * 28 / 32)} height={height} viewBox="0 0 28 32" aria-hidden="true" focusable="false">
      <path d={ATTACH_DOC_PATH} fill="#fff" stroke={color} strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M17 2v5a2 2 0 0 0 2 2h5z" fill={ATTACH_TYPE_BG[ext] || C.neutralBg} stroke={color} strokeWidth="1.3" strokeLinejoin="round" />
      {ext === "pdf" && (
        <>
          <rect x="8" y="14" width="5" height="5" rx="1" fill={color} />
          <path d="M15.5 15h4.5M15.5 18h4.5M8 22h12M8 25h8" {...line} />
        </>
      )}
      {ext === "docx" && <path d="M8 15h12M8 18.5h12M8 22h12M8 25.5h7" {...line} />}
      {ext === "xlsx" && (
        <>
          <rect x="8" y="14" width="12" height="11" rx="1" {...line} strokeWidth="1.2" />
          <path d="M8 17.7h12M8 21.3h12M12 14v11M16 14v11" {...line} strokeWidth="1" />
        </>
      )}
      {ext === "pptx" && (
        <>
          <rect x="8" y="13.5" width="12" height="9" rx="1" {...line} strokeWidth="1.2" />
          <circle cx="12" cy="18" r="2" fill={color} />
          <path d="M15.5 16.5h3M15.5 19.5h3M14 22.5v3M11 26h6" {...line} strokeWidth="1.2" />
        </>
      )}
    </svg>
  );
}

// 첨부 카드 한 장: 형식 아이콘 + 파일명 + 확장자·크기 + 제거.
//   status "reading" = 읽는 중(아이콘이 흐리고 아래 가장자리에 진행선), "ready" = 다 읽음, "leaving" = 제거되어 접히는 중
//   새로 붙으면 아래에서 살짝 올라오며 나타나고, 다 읽은 직후(justRead)에는 체크가 잠깐 튀어나온다
function AttachedFileCard({ name, bytes, status, progress = 0, justRead, onRemove, removeLabel }) {
  const { t } = useTranslation();
  const ext     = extOf(name);
  const color   = ATTACH_TYPE_COLOR[ext] || C.neutral;
  const reading = status === "reading";
  const leaving = status === "leaving";
  return (
    <Box sx={{
      position: "relative", display: "flex", alignItems: "center", gap: 1, minWidth: 0, overflow: "hidden",
      width: { xs: "100%", sm: 210 },                       // 좁은 화면에서는 한 줄에 하나씩
      p: "6px 6px 6px 8px", borderRadius: 2, bgcolor: C.bg, border: `1px solid ${C.border}`,
      animation: "mktAttachIn 240ms cubic-bezier(.2,.8,.2,1)",
      "@keyframes mktAttachIn": { from: { opacity: 0, transform: "translateY(8px) scale(.96)" }, to: { opacity: 1, transform: "none" } },
      transition: ["border-color 150ms", "background-color 150ms",
        ...["opacity", "transform", "width", "margin", "padding"].map(p => `${p} ${ATTACH_EXIT_MS}ms`)].join(", "),
      "&:hover": { borderColor: `${color}70`, bgcolor: "#fff" },
      // 제거 중: 옆 카드가 자연스럽게 당겨지도록 폭·여백까지 0 으로 접는다 (mr -0.8 은 카드 사이 간격을 상쇄)
      ...(leaving && { opacity: 0, transform: "scale(.9)", pointerEvents: "none", borderColor: "transparent",
        width: { xs: "100%", sm: 0 }, px: { sm: 0 }, mr: { sm: -0.8 } }),
      [REDUCED_MOTION]: { animation: "none", transition: "none" },
    }}>
      <Box sx={{ flexShrink: 0, lineHeight: 0, opacity: reading ? 0.45 : 1, transition: "opacity 300ms",
        [REDUCED_MOTION]: { transition: "none" } }}>
        <AttachFileTypeIcon ext={ext} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography noWrap title={name} sx={{ fontSize: FONT.BODY, fontWeight: 600, color: C.text1, lineHeight: 1.4 }}>
          {name}
        </Typography>
        <Stack direction="row" spacing={0.8} alignItems="center" sx={{ fontSize: FONT.CAPTION, color: C.text3, lineHeight: 1.4 }}>
          <span style={{ color, fontWeight: 700 }}>{ext.toUpperCase()}</span>
          <span>{reading ? t("mktIntelAttachReadingPct", { pct: progress }) : fmtBytes(bytes)}</span>
          {justRead && (
            <CheckIcon sx={{ fontSize: 13, color: C.success, animation: "mktAttachPop 300ms",
              "@keyframes mktAttachPop": { "0%": { transform: "scale(0)" }, "60%": { transform: "scale(1.3)" }, "100%": { transform: "scale(1)" } },
              [REDUCED_MOTION]: { animation: "none" } }} />
          )}
        </Stack>
      </Box>
      {onRemove && (
        <IconButton size="small" onClick={onRemove} aria-label={removeLabel}
          sx={{ p: 0.3, color: C.text3, "&:hover": { color: C.text1 } }}>
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      )}
      {/* 읽는 동안만 보이는 진행선 */}
      <Box sx={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 2, opacity: reading ? 1 : 0,
        transition: "opacity 300ms", [REDUCED_MOTION]: { transition: "none" } }}>
        <Box sx={{ height: "100%", width: `${reading ? progress : 100}%`, bgcolor: color,
          transition: "width 120ms linear", [REDUCED_MOTION]: { transition: "none" } }} />
      </Box>
    </Box>
  );
}

// 문서를 끌고 들어왔을 때 작성 박스를 덮는 "여기에 놓기" 안내. 점선이 흐르고 문서 세 장이 부채꼴로 펼쳐진다.
//   마우스 이벤트를 받지 않는다(pointerEvents none) — 끌고 있는 파일은 아래의 작성 박스가 그대로 받는다
function AttachDropOverlay() {
  const { t } = useTranslation();
  const sheet = (name, to, zIndex) => ({
    position: "absolute", left: 21, top: 4, lineHeight: 0, zIndex,
    animation: `${name} 300ms cubic-bezier(.2,.9,.3,1.3) forwards`,
    [`@keyframes ${name}`]: { from: { transform: "none" }, to: { transform: to } },
    [REDUCED_MOTION]: { animation: "none", transform: to },
  });
  return (
    <Box sx={{
      position: "absolute", inset: "4px", zIndex: 1, borderRadius: "9px", pointerEvents: "none",
      display: "flex", alignItems: "center", justifyContent: "center", gap: 1.75, px: 1.5,
      bgcolor: `${C.primaryBg}F2`,
      animation: "mktDropIn 160ms ease-out",
      "@keyframes mktDropIn": { from: { opacity: 0 }, to: { opacity: 1 } },
      [REDUCED_MOTION]: { animation: "none" },
    }}>
      {/* 흐르는 점선 테두리 — 둥근 모서리를 따라가야 해서 CSS border 대신 SVG 로 그린다 */}
      <Box component="svg" aria-hidden="true" sx={{
        position: "absolute", inset: "1px", width: "calc(100% - 2px)", height: "calc(100% - 2px)", overflow: "visible",
        "& rect": { fill: "none", stroke: C.primary, strokeWidth: 1.5, strokeDasharray: "6 4", animation: "mktDropAnts 600ms linear infinite" },
        "@keyframes mktDropAnts": { to: { strokeDashoffset: -20 } },
        [REDUCED_MOTION]: { "& rect": { animation: "none" } },
      }}>
        <rect x="0" y="0" width="100%" height="100%" rx="8" />
      </Box>
      <Box sx={{
        position: "relative", width: 70, height: 40, flexShrink: 0,
        animation: "mktDropBob 1200ms ease-in-out infinite",
        "@keyframes mktDropBob": { "0%,100%": { transform: "translateY(0)" }, "50%": { transform: "translateY(-3px)" } },
        [REDUCED_MOTION]: { animation: "none" },
      }}>
        <Box sx={sheet("mktDropFanL", "translateX(-18px) rotate(-14deg)", 0)}><AttachFileTypeIcon ext="pdf" height={32} /></Box>
        <Box sx={sheet("mktDropFanC", "translateY(-3px)", 1)}><AttachFileTypeIcon ext="xlsx" height={32} /></Box>
        <Box sx={sheet("mktDropFanR", "translateX(18px) rotate(14deg)", 0)}><AttachFileTypeIcon ext="docx" height={32} /></Box>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: C.primary, lineHeight: 1.4 }}>
          {t("mktIntelAttachDropHere")}
        </Typography>
        <Typography sx={{ fontSize: FONT.CAPTION, color: C.text2, lineHeight: 1.4 }}>
          {t("mktIntelAttachHint")}
        </Typography>
      </Box>
    </Box>
  );
}

function CommentAndAnalyzeSection({
  userComment, setUserComment, analyzing, onAnalyze,
  btnLabel, apiError, onCloseError,
  contextFiles = [], setContextFiles,
}) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const pickSeqRef   = useRef(0);           // 문서를 고를 때마다 +1 되는 순번
  const pickOrderRef = useRef(new Map());   // 문서 이름 → 고른 순번. 늦게 다 읽힌 문서도 고른 순서대로 놓기 위해
  const readersRef   = useRef(new Map());   // 읽는 중인 FileReader. 카드를 제거하면 abort() 로 읽기를 멈춘다
  const timersRef    = useRef([]);          // 이 컴포넌트가 건 setTimeout — 화면을 떠날 때 모두 지운다
  const lastWarning  = useRef("");          // 경고가 접히는 동안에도 글이 남아 있도록 마지막 문구를 잡아 둔다
  const [readingFiles, setReadingFiles] = useState([]);    // 읽는 중인 문서 [{name, size, progress}]
  const [leavingFiles, setLeavingFiles] = useState([]);    // 제거되어 접히는 동안만 남겨 두는 카드 [{name, bytes}]
  const [justRead,     setJustRead]     = useState([]);    // 방금 다 읽은 문서 이름 — 카드에 체크가 잠깐 뜬다
  const [attachError,  setAttachError]  = useState(null);  // 거절·읽기 실패 경고 (여러 건이면 줄바꿈으로 잇는다)
  const [rejectTick,   setRejectTick]   = useState(0);     // 거절될 때마다 +1 — 작성 박스가 짧게 흔들린다
  const [rejectFlash,  setRejectFlash]  = useState(false); // 거절 직후 잠깐 테두리를 경고색으로
  const [dragDepth,    setDragDepth]    = useState(0);     // 파일을 끌고 들어온 깊이(enter − leave). 0 보다 크면 놓기 안내를 띄운다
  const [focused,      setFocused]      = useState(false); // 작성 박스 안(입력창·클립·카드 제거 버튼) 어딘가에 포커스가 있는지
  const [cue,          setCue]          = useState(0);     // 0 보다 크면 분석 버튼 아이콘이 한 번 반짝인다 (문서를 다 읽은 직후)

  // 화면을 떠날 때: 걸어 둔 타이머를 지우고 읽던 파일을 멈춘다
  useEffect(() => {
    const timers = timersRef.current, readers = readersRef.current;
    return () => { timers.forEach(clearTimeout); readers.forEach(r => r.abort()); };
  }, []);
  // 경고는 6초 뒤 스스로 닫힌다 (문구가 바뀌면 그때부터 다시 6초)
  useEffect(() => {
    if (!attachError) return undefined;
    const id = setTimeout(() => setAttachError(null), ATTACH_WARNING_MS);
    return () => clearTimeout(id);
  }, [attachError]);
  // 분석이 시작되면 남아 있던 반짝임 신호를 지운다 — 분석이 끝나 아이콘이 다시 그려질 때 또 반짝이지 않게
  useEffect(() => { if (analyzing) setCue(0); }, [analyzing]);

  const isReading  = readingFiles.length > 0;
  const blocked    = analyzing || isReading;
  const dragging   = dragDepth > 0 && !analyzing;
  const totalBytes = contextFiles.reduce((s, c) => s + b64Bytes(c.content_b64), 0)
                   + readingFiles.reduce((s, r) => s + r.size, 0);
  const capPct     = Math.min(100, totalBytes / ATTACH_MAX_TOTAL_BYTES * 100);
  const capColor   = capPct >= 95 ? C.danger : capPct >= 80 ? C.warning : C.primary;

  const later = (fn, ms) => { timersRef.current.push(setTimeout(fn, ms)); };
  // 경고를 띄우고 작성 박스를 흔든다. append=true 면 떠 있는 경고 뒤에 잇는다 (읽다 실패한 문서는 뒤늦게 하나씩 오므로)
  const warn = (lines, append = false) => {
    setAttachError(prev => [append ? prev : null, ...lines].filter(Boolean).join("\n"));
    setRejectTick(n => n + 1);
    setRejectFlash(true);
    later(() => setRejectFlash(false), 700);
  };
  const settleReading = (name) => {
    readersRef.current.delete(name);
    setReadingFiles(prev => prev.filter(r => r.name !== name));
  };
  const pickOrder = (name) => pickOrderRef.current.get(name) ?? -1;
  const insertByPickOrder = (list, item) => {
    const at = list.findIndex(f => pickOrder(f.name) > pickOrder(item.name));
    return at < 0 ? [...list, item] : [...list.slice(0, at), item, ...list.slice(at)];
  };

  const addFiles = (picked) => {
    const taken       = new Set([...contextFiles.map(c => c.name), ...readingFiles.map(r => r.name)]);
    const unsupported = picked.filter(f => !ATTACH_EXTS.includes(extOf(f.name)));
    const tooLarge    = picked.filter(f => !unsupported.includes(f) && f.size > ATTACH_MAX_BYTES);
    // 합계는 이미 첨부된 것과 읽는 중인 것부터 더해 가며 순서대로 판정한다
    let total = totalBytes;
    const ok = [], overTotal = [];
    for (const f of picked) {
      if (unsupported.includes(f) || tooLarge.includes(f) || taken.has(f.name)) continue;
      if (total + f.size > ATTACH_MAX_TOTAL_BYTES) { overTotal.push(f); continue; }
      ok.push(f); total += f.size; taken.add(f.name);
    }
    const warnings = [
      ...unsupported.map(f => t("mktIntelAttachUnsupported", { name: f.name })),
      ...tooLarge.map(f => t("mktIntelAttachTooLarge", { name: f.name })),
      ...overTotal.map(f => t("mktIntelAttachTotalExceeded", { name: f.name })),
    ];
    if (warnings.length) warn(warnings); else setAttachError(null);
    if (!ok.length) return;

    ok.forEach(f => pickOrderRef.current.set(f.name, pickSeqRef.current++));
    setLeavingFiles(prev => prev.filter(g => !ok.some(f => f.name === g.name)));
    setReadingFiles(prev => [...prev, ...ok.map(f => ({ name: f.name, size: f.size, progress: 0 }))]);
    ok.forEach(f => {
      readFileAsBase64(f, {
        onReader:   (reader) => readersRef.current.set(f.name, reader),
        onProgress: (pct) => setReadingFiles(prev => prev.map(r => (r.name === f.name ? { ...r, progress: pct } : r))),
      })
        .then(b64 => {
          // '읽는 중'에서 빼는 것과 '보낼 목록'에 넣는 것을 같은 틱에 해야 카드가 한순간 두 장으로 보이지 않는다
          settleReading(f.name);
          setContextFiles(prev => insertByPickOrder(prev, { name: f.name, content_b64: b64 }));
          setJustRead(prev => [...prev, f.name]);
          later(() => setJustRead(prev => prev.filter(n => n !== f.name)), ATTACH_CHECK_MS);
          // 마지막 문서까지 다 읽혔다 — 분석 버튼이 열리면서 아이콘이 한 번 반짝인다
          if (readersRef.current.size === 0) setCue(n => n + 1);
        })
        .catch(err => {
          settleReading(f.name);
          if (err?.name !== "AbortError") warn([t("mktIntelAttachReadFailed", { name: f.name })], true);
        });
    });
  };
  const handlePickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";                                   // 같은 파일을 다시 고를 수 있게 input 을 비운다
    addFiles(picked);
  };
  // 탐색기에서 파일을 끌고 올 때만 반응한다 (입력창의 글자를 끄는 것은 브라우저 기본 동작에 맡긴다).
  //   enter/leave 는 자식 요소를 지날 때마다 짝지어 오므로, 켜고 끄는 대신 깊이를 세어야 안내가 깜빡이지 않는다
  const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");
  const handleDragEnter = (e) => { if (!hasFiles(e)) return; e.preventDefault(); if (!analyzing) setDragDepth(d => d + 1); };
  const handleDragLeave = (e) => { if (hasFiles(e)) setDragDepth(d => Math.max(0, d - 1)); };
  const handleDragOver  = (e) => { if (hasFiles(e)) e.preventDefault(); };   // 여기에 놓을 수 있다고 브라우저에 알린다
  const handleDrop = (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault(); setDragDepth(0);
    if (analyzing) return;
    addFiles(Array.from(e.dataTransfer?.files || []));
  };
  // 보낼 목록에서는 바로 빼고, 카드는 접히는 동안만 남겨 둔다. 읽는 중이던 문서면 읽기도 멈춘다
  const removeFile = (name) => {
    const ready   = contextFiles.find(f => f.name === name);
    const pending = readingFiles.find(r => r.name === name);
    readersRef.current.get(name)?.abort();
    setContextFiles(prev => prev.filter(f => f.name !== name));
    setReadingFiles(prev => prev.filter(r => r.name !== name));
    setLeavingFiles(prev => [...prev, { name, bytes: ready ? b64Bytes(ready.content_b64) : (pending?.size ?? 0) }]);
    later(() => setLeavingFiles(prev => prev.filter(g => g.name !== name)), ATTACH_EXIT_MS + 20);
  };

  // 카드 줄: 다 읽은 문서 + 읽는 중 + 접히는 중을 고른 순서대로 섞어 놓는다
  const cards = [
    ...contextFiles.map(f => ({ name: f.name, bytes: b64Bytes(f.content_b64), status: "ready" })),
    ...readingFiles.map(r => ({ name: r.name, bytes: r.size, status: "reading", progress: r.progress })),
    ...leavingFiles.map(g => ({ ...g, status: "leaving" })),
  ].sort((a, b) => pickOrder(a.name) - pickOrder(b.name));

  // 작성 박스 테두리: 거절 직후(경고색) > 끌어오는 중 > 포커스(연한 회색 + 3px 링) > 평소
  //   포커스는 CSS :focus-within 대신 상태(focused)로 잡는다 — jsdom 이 :focus-within 규칙을 만나면 getComputedStyle 에서 죽어 테스트가 전부 실패한다
  const borderColor = rejectFlash ? C.warning : dragging ? `${C.primary}4D` : focused ? `${C.neutral}9E` : `${C.neutral}30`;
  const ring        = rejectFlash ? `0 0 0 3px ${C.warning}24` : (focused && !dragging) ? `0 0 0 3px ${C.neutral}1A` : "none";
  const quiet       = !rejectFlash && !dragging && !focused;     // 거절·드래그·포커스 표시가 없을 때만 호버 색이 적용된다
  const analyzeGradient = `linear-gradient(135deg, ${C.primary}, #7c3aed)`;
  if (attachError) lastWarning.current = attachError;

  return (
    <>
      <Box sx={{ mb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={0.6} mb={0.8}>
          <EditNoteIcon sx={{ fontSize: 15, color: C.neutral }} />
          <Typography variant="caption" fontWeight={700} color="text.secondary">
            {t("mktIntelExtraContext")}
          </Typography>
          <Chip label={t("mktIntelOptionalInput")} size="small"
            sx={{ height: 16, fontSize: 9, fontWeight: 600,
              bgcolor: C.neutralBg, color: C.neutral }} />
        </Stack>
        <input ref={fileInputRef} data-testid="mkt-context-file-input" type="file" hidden multiple accept={ATTACH_ACCEPT}
          onChange={handlePickFiles} />
        {/* ── 작성 박스: 테두리·포커스·드롭 영역을 한 겹이 맡는다. 안쪽 textarea 는 테두리 없음.
               onFocus/onBlur 는 자식에서 버블링되므로 입력창·클립·카드 제거 어디에 포커스가 있어도 박스가 반응한다.
               blur 는 포커스가 박스 안에서 옮겨 갈 때도 오므로, 새 포커스(relatedTarget)가 박스 밖일 때만 끈다 ── */}
        <Box
          data-testid="mkt-context-composer" data-drag={dragging} data-focused={focused}
          onFocus={() => setFocused(true)}
          onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget)) setFocused(false); }}
          onDragEnter={handleDragEnter} onDragLeave={handleDragLeave} onDragOver={handleDragOver} onDrop={handleDrop}
          sx={{
            position: "relative", bgcolor: "#fff", borderRadius: 3, overflow: "hidden",
            border: `1px solid ${borderColor}`, boxShadow: ring,
            transition: "border-color 160ms, box-shadow 200ms, opacity 200ms",
            "&:hover": quiet ? { borderColor: `${C.neutral}60` } : {},
            opacity: analyzing ? 0.7 : 1,
            // 거절될 때마다 이름이 다른 같은 키프레임으로 바꿔 끼워, 박스를 다시 그리지 않고도 흔들림이 매번 재생되게 한다
            animation: rejectTick ? `mktAttachShake${rejectTick % 2} 360ms` : "none",
            "@keyframes mktAttachShake0": ATTACH_SHAKE,
            "@keyframes mktAttachShake1": ATTACH_SHAKE,
            [REDUCED_MOTION]: { transition: "none", animation: "none" },
          }}>
          {cards.length > 0 && (
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.8} sx={{ p: "10px 10px 0" }}>
              {cards.map(c => (
                <AttachedFileCard key={c.name} {...c} justRead={justRead.includes(c.name)} removeLabel={t("remove")}
                  onRemove={analyzing || c.status === "leaving" ? undefined : () => removeFile(c.name)} />
              ))}
            </Stack>
          )}
          <InputBase
            fullWidth multiline minRows={2} maxRows={4}
            placeholder={t("mktIntelCommentPlaceholder")}
            value={userComment}
            onChange={e => setUserComment(e.target.value)}
            disabled={analyzing}
            sx={{ px: 1.5, pt: 1.2, pb: 0.6, fontSize: FONT.SUB, "& textarea": { lineHeight: 1.7 } }}
          />
          {/* 하단 바: 왼쪽 클립(호버 시 안내 툴팁), 오른쪽 합계 용량(문서가 있을 때만). 좁으면 줄바꿈 */}
          <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap
            sx={{ px: 1, pb: 0.5, gap: 1 }}>
            <Tooltip title={t("mktIntelAttachHint")} placement="top-start" arrow>
              <span>{/* disabled 버튼은 이벤트를 안 내므로 span 으로 감싸야 툴팁이 뜬다 */}
                <IconButton size="small" aria-label={t("mktIntelAttachFile")}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={analyzing}
                  sx={{ p: 0.6, color: C.neutral, transition: "transform 120ms, color 120ms",
                    "& svg": { transition: "transform 180ms" },
                    "&:hover": { color: C.primary, bgcolor: C.primaryBg },
                    "&:hover svg": { transform: "rotate(-18deg)" },
                    "&:active": { transform: "scale(0.86)" },
                    [REDUCED_MOTION]: { transition: "none", "& svg": { transition: "none" },
                      "&:hover svg": { transform: "none" }, "&:active": { transform: "none" } } }}>
                  <AttachFileIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </span>
            </Tooltip>
            {cards.length > 0 && (
              <Stack direction="row" alignItems="center" spacing={0.8} sx={{ ml: "auto" }}>
                <Box sx={{ width: 56, height: 4, borderRadius: 2, bgcolor: C.border, overflow: "hidden" }}>
                  <Box sx={{ height: "100%", borderRadius: 2, width: `${capPct}%`, bgcolor: capColor,
                    transition: "width 350ms cubic-bezier(.2,.8,.2,1), background-color 200ms",
                    [REDUCED_MOTION]: { transition: "none" } }} />
                </Box>
                <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3, fontVariantNumeric: "tabular-nums" }}>
                  {fmtBytes(totalBytes)} / {ATTACH_MAX_TOTAL_BYTES / 1024 / 1024}MB
                </Typography>
              </Stack>
            )}
          </Stack>
          {dragging && <AttachDropOverlay />}
        </Box>
        {/* 거절·읽기 실패 경고: 아래로 펼쳐졌다가 6초 뒤 스스로 접힌다 */}
        <Collapse in={!!attachError} timeout={ATTACH_EXIT_MS} unmountOnExit>
          <Stack direction="row" alignItems="center" spacing={0.8} role="alert"
            sx={{ mt: 1, p: "6px 6px 6px 10px", borderRadius: 2, bgcolor: C.warningBg }}>
            <WarningAmberIcon sx={{ fontSize: 15, color: C.warning, flexShrink: 0 }} />
            <Typography sx={{ flex: 1, minWidth: 0, fontSize: FONT.BODY, color: C.text1, lineHeight: 1.5,
              whiteSpace: "pre-line", wordBreak: "break-word" }}>
              {lastWarning.current}
            </Typography>
            <IconButton size="small" aria-label={t("close")} onClick={() => setAttachError(null)}
              sx={{ p: 0.3, color: C.text3, "&:hover": { color: C.text1 } }}>
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Stack>
        </Collapse>
      </Box>
      {/* 분석 버튼: 그라데이션을 ::before 에 깔아 두고 막힐 때 0.2초에 걸쳐 걷어 낸다 —
            작은 문서는 수십 ms 만에 읽히므로, 바로 회색으로 바꾸면 버튼이 번쩍인다 */}
      <Button
        fullWidth variant="contained" size="medium" data-cue={cue > 0}
        startIcon={blocked
          ? <CircularProgress size={16} color="inherit" />
          : <AutoAwesomeIcon key={cue} onAnimationEnd={() => setCue(0)}
              sx={{ animation: cue ? "mktAnalyzeCue 600ms ease-in-out 1" : "none",
                "@keyframes mktAnalyzeCue": { "0%,100%": { transform: "none" }, "45%": { transform: "scale(1.4) rotate(20deg)" } },
                [REDUCED_MOTION]: { animation: "none" } }} />}
        onClick={onAnalyze}
        disabled={blocked}
        sx={{
          fontWeight: 700, borderRadius: 2, position: "relative", zIndex: 0, overflow: "hidden",
          "&::before": { content: '""', position: "absolute", inset: 0, zIndex: -1, background: analyzeGradient,
            opacity: blocked ? 0 : 1, transition: "opacity 200ms" },
          [REDUCED_MOTION]: { "&::before": { transition: "none" } },
        }}>
        {analyzing ? t("mktIntelAnalyzing") : isReading ? t("mktIntelAttachReadingBtn") : (btnLabel || t("mktIntelAnalyzeBtn"))}
      </Button>
      {apiError && (
        <Alert severity="error" sx={{ mt: 1.5, py: 0.5, borderRadius: 1.5 }}
          onClose={onCloseError}>{apiError}</Alert>
      )}
    </>
  );
}
// 첨부 동작 테스트용 (pages/tests/ai/AiMarketingIntelAttach.test.js). 시간 상수는 테스트가 타이머를 감을 때 같은 값을 쓰기 위해 내보낸다
export { CommentAndAnalyzeSection, ATTACH_WARNING_MS, ATTACH_EXIT_MS };

// ── 수요 영향 품목 행 ──────────────────────────────────────────────────────
function ImpactRow({ item }) {
  const { t } = useTranslation();
  const d = Number(item.demand_change_pct || 0);
  const dColor = d > 0 ? C.success : d < 0 ? C.danger : C.neutral;
  const DIcon  = d > 0 ? TrendingUpIcon : d < 0 ? TrendingDownIcon : RemoveIcon;
  const qtyDelta = item.estimated_qty_delta != null ? Number(item.estimated_qty_delta) : null;
  const amtDelta = item.estimated_amt_delta != null ? Number(item.estimated_amt_delta) : null;
  const slPct    = item.sl_pct != null ? Number(item.sl_pct) : null;
  const invGap   = item.inv_gap_qty != null ? Number(item.inv_gap_qty) : null;
  const dpNeeded = item.dp_adjustment_needed;
  const dpRec    = item.dp_recommendation;

  // 실적 vs DP계획 비교 — 모두 3M 기준으로 통일
  const avg3m   = item.avg_3m_qty    != null ? Number(item.avg_3m_qty)    : null;
  const avg3mMf = item.avg_3m_mf_qty != null ? Number(item.avg_3m_mf_qty) : null;
  const planVsActualPct = (avg3m && avg3m > 0 && avg3mMf != null)
    ? ((avg3mMf - avg3m) / avg3m * 100) : null;

  // 권고 조치: \n 기준으로 핵심 + 근거 분리
  const actionLines = (item.action || "").split("\n");
  const actionMain  = actionLines[0] || "";
  const actionEvidence = actionLines.slice(1).join(" ").trim();

  return (
    <TableRow hover>
      <TableCell sx={{ py: 0.8 }}>
        <Typography variant="body2" fontWeight={700} lineHeight={1.3}>{item.item_name}</Typography>
        <Typography variant="caption" color="text.secondary">{item.item_cd}</Typography>
      </TableCell>
      <TableCell sx={{ py: 0.8 }}>
        <Chip label={item.class || item.inv_class_cd?.[0] || "?"} size="small"
          sx={{ fontWeight: 700, fontSize: 10,
            bgcolor: (item.class || item.inv_class_cd?.[0]) === "A" ? "#dbeafe"
                   : (item.class || item.inv_class_cd?.[0]) === "B" ? "#fef9c3" : C.neutralBg,
            color:   (item.class || item.inv_class_cd?.[0]) === "A" ? "#1d4ed8"
                   : (item.class || item.inv_class_cd?.[0]) === "B" ? "#713f12" : C.neutral }} />
      </TableCell>
      <TableCell sx={{ py: 0.8 }}>
        <Stack direction="row" alignItems="center" spacing={0.4}>
          <DIcon sx={{ fontSize: 14, color: dColor }} />
          <Typography variant="body2" fontWeight={700} sx={{ color: dColor }}>
            {d !== 0 ? `${d > 0 ? "+" : ""}${d.toFixed(0)}%` : "-"}
          </Typography>
        </Stack>
        <LinearProgress variant="determinate"
          value={Math.min(Math.abs(d) * 2.5, 100)}
          sx={{ height: 3, borderRadius: 2, mt: 0.3, width: 64,
            bgcolor: `${dColor}20`, "& .MuiLinearProgress-bar": { bgcolor: dColor } }} />
        {qtyDelta != null && (
          <Typography variant="caption" sx={{ fontSize: 9, color: dColor }}>
            {qtyDelta > 0 ? "+" : ""}{qtyDelta.toFixed(0)}EA
            {amtDelta != null && amtDelta !== 0 && (
              <span style={{ marginLeft: 3, color: C.neutral }}>
                ({fmtSignedMoney(amtDelta)})
              </span>
            )}
          </Typography>
        )}
        {/* ── 실적 vs DP계획 비교 ── */}
        {avg3m != null && (
          <Typography variant="caption" display="block" sx={{ fontSize: 9, color: C.neutral, mt: 0.3 }}>
            {t("mktIntelActual3m")} {avg3m.toLocaleString("ko-KR", { maximumFractionDigits: 0 })}EA
            {avg3mMf != null && (
              <span style={{
                marginLeft: 3,
                color: planVsActualPct != null
                  ? (planVsActualPct > 5 ? C.success : planVsActualPct < -5 ? C.danger : C.neutral)
                  : C.neutral,
                fontWeight: 600,
              }}>
                {" / "}{t("mktIntelDpPlan")}{" "}{avg3mMf.toLocaleString("ko-KR", { maximumFractionDigits: 0 })}EA
                {planVsActualPct != null && (
                  <span>({planVsActualPct > 0 ? "+" : ""}{planVsActualPct.toFixed(0)}%)</span>
                )}
              </span>
            )}
          </Typography>
        )}
      </TableCell>
      <TableCell sx={{ py: 0.8 }}>
        <Typography variant="caption" color="text.secondary">
          {item.current_inv_days != null ? t("mpDayCountLabel", { count: item.current_inv_days }) : "-"}
        </Typography>
        {slPct != null && (
          <Typography variant="caption" display="block"
            sx={{ fontSize: 9, color: slPct < 80 ? C.danger : slPct < 90 ? C.warning : C.success,
              fontWeight: slPct < 85 ? 700 : 400 }}>
            SL {slPct.toFixed(0)}%
          </Typography>
        )}
        {invGap != null && invGap < 0 && (
          <Typography variant="caption" display="block" sx={{ fontSize: 9, color: C.danger, fontWeight: 700 }}>
            {t("mktIntelInvGap", { v: invGap.toFixed(0) })}
          </Typography>
        )}
      </TableCell>
      <TableCell sx={{ py: 0.8 }}><RiskChip level={item.risk || item.risk_level} /></TableCell>
      <TableCell sx={{ py: 0.8 }}>
        {/* 핵심 권고 */}
        <Typography variant="caption" sx={{ color: C.primary, fontWeight: 600, lineHeight: 1.4 }}>
          {actionMain}
        </Typography>
        {/* 근거 수치 (작은 글씨) */}
        {actionEvidence && (
          <Typography variant="caption" display="block"
            sx={{ fontSize: 9, color: C.neutral, mt: 0.2, lineHeight: 1.3 }}>
            {actionEvidence}
          </Typography>
        )}
        {/* DP 조정 배지 */}
        {dpNeeded && dpRec && (
          <Tooltip title={dpRec} placement="top" arrow>
            <Chip
              label={t("mktIntelDpAdjustNeeded")}
              size="small"
              sx={{
                mt: 0.4, display: "flex", height: 16, fontSize: 9, fontWeight: 700, cursor: "pointer",
                bgcolor: d > 0 ? C.warningBg : C.dangerBg,
                color:   d > 0 ? C.warning  : C.danger,
                border: `1px solid ${d > 0 ? C.warning : C.danger}40`,
              }}
            />
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
}

// ── 분석 인사이트 카드 ────────────────────────────────────────────────────
function DemandInsightCard({
  event, intel, items, dataNotice, isSignalOnly,
  keyFindings = [], userComment, isDbAnalysis = false,
}) {
  const { t } = useTranslation();
  const [showMetrics, setShowMetrics] = useState(!HIDE_IMPACT_METRICS);
  if (!intel) return null;
  const dPct    = event.demand_impact_pct;
  const supPct  = event.supply_impact_pct;

  // 자사 품목 기반 통계 계산
  const stockoutCount  = items.filter(i => i.stockout_flg === "Y" || i.risk === "HIGH").length;
  const highRiskItems  = items.filter(i => i.risk === "HIGH" || i.risk_level === "HIGH");
  const dpAdjItems     = items.filter(i => i.dp_adjustment_needed === true);
  const totalAmtDelta  = items.reduce((s, i) => s + (Number(i.estimated_amt_delta) || 0), 0);
  const avgDemandChg   = items.length
    ? (items.reduce((s, i) => s + (parseFloat(i.demand_change_pct) || 0), 0) / items.length).toFixed(1)
    : null;

  // signal_impact 기반 자사 요약 (recommendation_summary) 우선,
  // 없으면 keyFindings 첫 항목, 일반 시장 뉴스(description)는 여기에 표시 안 함
  const companySummary = intel.recommendation_summary
    || (keyFindings.length > 0 ? null : event.recommended_action)
    || null;

  // 핵심 발견사항 (bulletPoint 표시) — 개수는 분석 내용에 따라 가변, 상한은 프롬프트에서 관리
  const bulletFindings = [...new Set(keyFindings.filter(Boolean))];

  return (
    <Stack spacing={1.5}>
      {/* ── AI 수요 영향 분석 헤더 박스 ── */}
      <Box sx={{ bgcolor: C.primaryBg, borderRadius: 2, p: "12px 14px", border: `1px solid ${C.primary}20` }}>

        {/* 헤더 행 */}
        <Stack direction="row" alignItems="center" spacing={0.8} mb={1.2} flexWrap="wrap" useFlexGap>
          <AutoAwesomeIcon sx={{ color: C.primary, fontSize: 16, flexShrink: 0 }} />
          <Typography variant="subtitle2" fontWeight={700} color={C.primary}>{t("mktIntelAiDemandImpactTitle")}</Typography>
          {intel.risk_level && <RiskChip level={intel.risk_level} />}
          {isDbAnalysis && (
            <Chip label={t("mktIntelBadgeDbBased")} size="small"
              sx={{ ml: "auto !important", height: 18, fontSize: 9, fontWeight: 700,
                bgcolor: C.successBg, color: C.success, border: `1px solid ${C.success}40` }} />
          )}
          {!isDbAnalysis && isSignalOnly && (
            <Chip label={t("mktIntelBadgeSignalInferred")} size="small"
              sx={{ ml: "auto !important", height: 18, fontSize: 9, fontWeight: 700,
                bgcolor: C.warningBg, color: C.warning, border: `1px solid ${C.warning}40` }} />
          )}
          {!isDbAnalysis && !isSignalOnly && (
            <Chip label={t("mktIntelBadgeMarketSignal")} size="small"
              sx={{ ml: "auto !important", height: 18, fontSize: 9, fontWeight: 600,
                bgcolor: C.tealBg, color: C.teal }} />
          )}
        </Stack>

        {/* ── 자사 현황 인라인 통계 (DB 기반) ── */}
        {items.length > 0 && (
          <Stack direction="row" spacing={1.5} mb={1.2} flexWrap="wrap" useFlexGap
            sx={{ p: "7px 10px", bgcolor: "background.paper", borderRadius: 1.5,
              border: `1px solid ${C.border}` }}>
            <Box>
              <Typography variant="caption" color="text.disabled" fontSize={9.5} display="block" fontWeight={600}>
                {t("mktIntelStatImpactItems")}
              </Typography>
              <Typography variant="body2" fontWeight={800} color={C.primary} fontSize={14}>
                {t("dpAttentionCountLabel", { count: items.length })}
              </Typography>
            </Box>
            {stockoutCount > 0 && (
              <Box sx={{ borderLeft: `1px solid ${C.border}`, pl: 1.5 }}>
                <Typography variant="caption" color="text.disabled" fontSize={9.5} display="block" fontWeight={600}>
                  {t("mktIntelStockoutBadge")}
                </Typography>
                <Typography variant="body2" fontWeight={800} color={C.danger} fontSize={14}>
                  {t("dpAttentionCountLabel", { count: stockoutCount })}
                </Typography>
              </Box>
            )}
            {highRiskItems.length > 0 && (
              <Box sx={{ borderLeft: `1px solid ${C.border}`, pl: 1.5 }}>
                <Typography variant="caption" color="text.disabled" fontSize={9.5} display="block" fontWeight={600}>
                  {t("mktIntelStatHighRisk")}
                </Typography>
                <Typography variant="body2" fontWeight={800} color={C.warning} fontSize={14}>
                  {t("dpAttentionCountLabel", { count: highRiskItems.length })}
                </Typography>
              </Box>
            )}
            {avgDemandChg !== null && (
              <Box sx={{ borderLeft: `1px solid ${C.border}`, pl: 1.5 }}>
                <Typography variant="caption" color="text.disabled" fontSize={9.5} display="block" fontWeight={600}>
                  {t("mktIntelStatAvgDemandChange")}
                </Typography>
                <Typography variant="body2" fontWeight={800} fontSize={14}
                  sx={{ color: parseFloat(avgDemandChg) > 0 ? C.success : parseFloat(avgDemandChg) < 0 ? C.danger : C.neutral }}>
                  {parseFloat(avgDemandChg) > 0 ? "+" : ""}{avgDemandChg}%
                </Typography>
              </Box>
            )}
            {totalAmtDelta !== 0 && (
              <Box sx={{ borderLeft: `1px solid ${C.border}`, pl: 1.5 }}>
                <Typography variant="caption" color="text.disabled" fontSize={9.5} display="block" fontWeight={600}>
                  {t("mktIntelStatAmtImpact")}
                </Typography>
                <Typography variant="body2" fontWeight={800} fontSize={13}
                  sx={{ color: totalAmtDelta > 0 ? C.success : C.danger }}>
                  {fmtSignedMoney(totalAmtDelta)}
                </Typography>
              </Box>
            )}
            {dpAdjItems.length > 0 && (
              <Box sx={{ borderLeft: `1px solid ${C.border}`, pl: 1.5 }}>
                <Typography variant="caption" color="text.disabled" fontSize={9.5} display="block" fontWeight={600}>
                  {t("mktIntelStatDpAdjust")}
                </Typography>
                <Typography variant="body2" fontWeight={800} color={C.warning} fontSize={14}>
                  {t("dpAttentionCountLabel", { count: dpAdjItems.length })}
                </Typography>
              </Box>
            )}
            {/* 가장 위험한 품목 1개 강조 */}
            {highRiskItems[0] && (
              <Box sx={{ borderLeft: `1px solid ${C.border}`, pl: 1.5, flexGrow: 1, minWidth: 0 }}>
                <Typography variant="caption" color="text.disabled" fontSize={9.5} display="block" fontWeight={600}>
                  {t("mktIntelStatTopItem")}
                </Typography>
                <Typography variant="body2" fontWeight={700} fontSize={12}
                  sx={{ color: C.danger, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {highRiskItems[0].item_name}
                </Typography>
              </Box>
            )}
          </Stack>
        )}

        {/* ── 핵심 발견사항 bullet ── */}
        {bulletFindings.length > 0 && (
          <Stack spacing={0.6} mb={companySummary ? 0.8 : 0}>
            {bulletFindings.map((f, i) => (
              <Stack key={i} direction="row" spacing={0.7} alignItems="flex-start">
                <Box sx={{
                  minWidth: 18, height: 18, borderRadius: "50%", mt: "1px", flexShrink: 0,
                  bgcolor: i === 0 ? C.primary : `${C.primary}25`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Typography sx={{ fontSize: 9, color: i === 0 ? "#fff" : C.primary, fontWeight: 800, lineHeight: 1 }}>
                    {i + 1}
                  </Typography>
                </Box>
                <Typography variant="body2" fontSize={12.5} lineHeight={1.6} color="text.secondary">{f}</Typography>
              </Stack>
            ))}
          </Stack>
        )}

        {/* ── 자사 요약 (signal_impact summary) — bullet 없을 때만 ── */}
        {companySummary && bulletFindings.length === 0 && (
          <Typography variant="body2" color="text.secondary" lineHeight={1.75}
            sx={{ whiteSpace: "pre-line" }}>
            {companySummary}
          </Typography>
        )}

        {/* ── 사용자 추가 컨텍스트 ── */}
        {userComment && (
          <Box sx={{ mt: 1, pt: 1, borderTop: `1px dashed ${C.border}` }}>
            <Stack direction="row" spacing={0.8} alignItems="flex-start">
              <EditNoteIcon sx={{ fontSize: 13, color: C.purple, mt: "2px", flexShrink: 0 }} />
              <Box>
                <Typography variant="caption" fontWeight={700} color={C.purple}
                  sx={{ display: "block", fontSize: 10 }}>{t("mktIntelUserContext")}</Typography>
                <Typography variant="body2" lineHeight={1.6} fontSize={12.5}
                  sx={{ whiteSpace: "pre-wrap" }}>{userComment}</Typography>
              </Box>
            </Stack>
          </Box>
        )}
      </Box>

      <Box>
        {HIDE_IMPACT_METRICS && (
          <MetricRevealToggle open={showMetrics} onToggle={() => setShowMetrics(v => !v)} sx={{ mb: 0.4 }} />
        )}
        <Collapse in={showMetrics}>
          <Grid container spacing={1}>
            <Grid item xs={6} sm={supPct < 0 ? 4 : 6}>
              <Card variant="outlined" sx={{ borderRadius: 1.5,
                bgcolor: dPct > 0 ? C.successBg : dPct < 0 ? C.dangerBg : C.neutralBg }}>
                <CardContent sx={{ p: "8px 12px !important" }}>
                  <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>{t("mktIntelDemandForecastChange")}</Typography>
                  <Typography variant="h6" fontWeight={800}
                    sx={{ color: dPct > 0 ? C.success : dPct < 0 ? C.danger : C.neutral }}>
                    {dPct > 0 ? "+" : ""}{dPct.toFixed(0)}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            {supPct < 0 && (
              <Grid item xs={6} sm={4}>
                <Card variant="outlined" sx={{ borderRadius: 1.5, bgcolor: C.purpleBg }}>
                  <CardContent sx={{ p: "8px 12px !important" }}>
                    <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>{t("bfFeatureSupplyRiskLabel")}</Typography>
                    <Typography variant="h6" fontWeight={800} sx={{ color: C.purple }}>
                      {supPct.toFixed(0)}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
            <Grid item xs={6} sm={supPct < 0 ? 4 : 6}>
              <Card variant="outlined" sx={{ borderRadius: 1.5 }}>
                <CardContent sx={{ p: "8px 12px !important" }}>
                  <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>{t("mktIntelImpactPeriod")}</Typography>
                  <Typography variant="h6" fontWeight={800}>{t("dpMonthsValueLabel", { v: event.effective_months })}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Collapse>
      </Box>

      <Card variant="outlined" sx={{ borderRadius: 2,
        border: `1px solid ${C.primary}30`, bgcolor: `${C.primary}06` }}>
        <CardContent sx={{ p: "10px 14px !important" }}>
          <Box>
            <Typography variant="caption" fontWeight={700} sx={{ color: C.primary }} display="block" mb={0.3}>
              {t("mktIntelRecommendedAction")}
            </Typography>
            <Typography variant="body2" color="text.secondary" lineHeight={1.65}>
              {event.recommended_action}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {items.length > 0 && (
        <>
          <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
            <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
              {t("mktIntelItemImpactDetail")}
            </Typography>
            {items[0]?.estimated_qty_delta != null && !isSignalOnly && (
              <Chip label={t("mktIntelBadgeRealSales")} size="small"
                sx={{ height: 16, fontSize: 9, fontWeight: 700,
                  bgcolor: C.successBg, color: C.success }} />
            )}
            {isSignalOnly && (
              <Chip label={t("mktIntelBadgeSignalItems")} size="small"
                sx={{ height: 16, fontSize: 9, fontWeight: 700,
                  bgcolor: C.warningBg, color: C.warning,
                  border: `1px solid ${C.warning}40` }} />
            )}
          </Stack>
          {dataNotice && (
            <Alert severity="warning" icon={false}
              sx={{ py: 0.5, px: 1.5, mb: 1, borderRadius: 1.5, fontSize: 11.5,
                border: `1px solid ${C.warning}40`, bgcolor: C.warningBg,
                color: C.warning, "& .MuiAlert-message": { lineHeight: 1.6 } }}>
              {dataNotice}
            </Alert>
          )}
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: C.bg }}>
                    {[t("item"), t("class"), t("mktIntelColDemandChange"), t("mktIntelColCurrentInvDays"), t("mktIntelColRisk"), t("mktIntelColAction")].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11, color: C.neutral }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map(item => <ImpactRow key={item.item_cd} item={item} />)}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </>
      )}
    </Stack>
  );
}

// ── 메인 컴포넌트 ─────────────────────────────────────────────────────────
function AnalyzingCard() {
  const { t } = useTranslation();
  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent sx={{ p: "16px !important" }}>
        <Stack spacing={1.5}>
          {[
            { label: t("mktIntelStepSignalsDone"), done: true },
            { label: t("mktIntelStepQueryingItems"), done: false },
            { label: t("mktIntelStepGeneratingActions"), done: false },
          ].map((s, i) => (
            <Stack key={i} direction="row" spacing={1} alignItems="center">
              {s.done
                ? <CheckCircleIcon sx={{ fontSize: 16, color: C.success }} />
                : <CircularProgress size={14} sx={{ color: C.primary }} />}
              <Typography variant="body2" color={s.done ? "text.primary" : "text.secondary"}
                fontWeight={s.done ? 600 : 400}>{s.label}</Typography>
            </Stack>
          ))}
          <LinearProgress sx={{ borderRadius: 2, height: 3 }} />
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function AiMarketingIntel({ viewName = "AI_MARKETING_INTEL" }) {
  const { t, i18n } = useTranslation();
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch  = useDispatch();
  const { isPermitted } = useMenuAccess();

  const [analyzing,        setAnalyzing]        = useState(false);
  const [result,           setResult]           = useState(reduxViewState?.result       ?? null);
  const [apiError,         setApiError]         = useState(null);
  const [userComment,      setUserComment]      = useState(reduxViewState?.userComment  ?? "");
  const [contextFiles,     setContextFiles]     = useState([]);
  // 단계: "idle" | "collecting" | "collected"
  const [liveStep,         setLiveStep]         = useState(reduxViewState?.liveStep     ?? "idle");
  const [collectedSignals, setCollectedSignals] = useState(reduxViewState?.collectedSignals ?? []);
  const [collectedEvent,   setCollectedEvent]   = useState(reduxViewState?.collectedEvent   ?? null);
  // 수요 영향 예상 품목 — 행 클릭 시 선정 근거 펼침 (인덱스, null=모두 닫힘)
  const [expandedImpactIdx, setExpandedImpactIdx] = useState(null);
  const [channelsUsed,     setChannelsUsed]     = useState(reduxViewState?.channelsUsed     ?? []);
  // 수집 결과에 딸린 기간 — 아래 설정값(collectPeriod*)과 달리 복원 시 덮어써진다
  const [collectedPeriodStart, setCollectedPeriodStart] = useState(reduxViewState?.collectedPeriodStart ?? null);
  const [collectedPeriodEnd, setCollectedPeriodEnd] = useState(reduxViewState?.collectedPeriodEnd ?? null);

  // ── 수집된 영향 품목 (ai_fact_demand 기반) ─────────────────────────────
  const [collectedImpact,  setCollectedImpact]  = useState(reduxViewState?.collectedImpact  ?? null);

  // ── 멀티 에이전트 수집·검증·분석 (Supervisor 파이프라인) ─────────────────
  const [agentBusy,   setAgentBusy]   = useState(false);
  const [agentError,  setAgentError]  = useState(null);
  // 실시간 진행 단계 (SSE tool_start/tool_done): [{tool, label, status: running|done|error}]
  const [agentSteps,  setAgentSteps]  = useState([]);
  // 기존 UI 병합용 — 에이전트 판단 기록 접힘 토글
  const [showAgentDecisions, setShowAgentDecisions] = useState(false);
  // 수집 요약 칩의 영향 평가 지표(수요 변동률·영향 기간) 노출 토글
  const [showEventMetrics, setShowEventMetrics] = useState(!HIDE_IMPACT_METRICS);

  // ── 품목 임베딩 인덱스 관리 (S4 의미 매핑) — plannel-support 계정에만 노출 ──
  const EMBED_ADMIN_USER = "plannel-support@zionex.com";
  const isEmbedAdmin = (authService.getCurrentUser()?.username || "")
    .toLowerCase() === EMBED_ADMIN_USER;
  const [embedStatus, setEmbedStatus] = useState(null);   // {embedded, total_items, last_updated}
  const [embedBusy,   setEmbedBusy]   = useState(false);
  // 키워드 분류(ai_item_keyword) — 임베딩의 선행 데이터 (분류 → 임베딩 순서 권장)
  const [clsStatus, setClsStatus] = useState(null);       // {classified_count, unclassified_count}
  const [clsBusy,   setClsBusy]   = useState(false);

  useEffect(() => {
    if (!isEmbedAdmin) return;
    aiScenarioDemoService.embeddingIndexStatus()
      .then(r => setEmbedStatus(r.data || null))
      .catch(() => setEmbedStatus(null));
    aiScenarioDemoService.classifyStatus()
      .then(r => setClsStatus(r.data || null))
      .catch(() => setClsStatus(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 통합 구축: 키워드 분류 → 임베딩 (순서 고정 — keyword_text 가 임베딩 재료).
  //   둘 다 증분·병렬이라 중단돼도 재클릭 시 이어짐.
  const handleMappingBuild = () => {
    if (embedBusy || clsBusy) return;
    setClsBusy(true); setEmbedBusy(true);
    aiScenarioDemoService.mappingDataBuild({})
      .then(() => Promise.all([
        aiScenarioDemoService.classifyStatus(),
        aiScenarioDemoService.embeddingIndexStatus(),
      ]))
      .then(([c, e]) => { setClsStatus(c?.data || null); setEmbedStatus(e?.data || null); })
      .catch(e => setApiError(apiErrorText(e, t("mktIntelMappingBuildError"))))
      .finally(() => { setClsBusy(false); setEmbedBusy(false); });
  };

  // ── 이전 기록 ────────────────────────────────────────────────────────────
  const [showHistory,     setShowHistory]     = useState(false);
  const [historySignals,  setHistorySignals]  = useState([]);
  const [historyLoading,  setHistoryLoading]  = useState(false);
  // 이전 기록 복원: DB(ai_market_signal/ai_demand_analysis_result)에서 전체 상태를 조회해 그대로 복원.
  const [restoringId,     setRestoringId]     = useState(null);

  const loadHistory = () => {
    if (historySignals.length > 0) { setShowHistory(v => !v); return; }
    setHistoryLoading(true);
    aiScenarioDemoService.getSignals(null, 20)
      .then(r => {
        setHistorySignals(r.data?.signals || []);
        setShowHistory(true);
      })
      .catch(() => setHistorySignals([]))
      .finally(() => setHistoryLoading(false));
  };

  // 이전 기록 클릭 → DB에서 처음 수집·분석했던 전체 상태를 조회해 그대로 복원(재수집 없음).
  // syncKeywords=false 는 방금 끝난 실행용 — 화면의 선택이 이미 정답이라 덮어쓰면 안 된다.
  const restoreFromDb = (signalId, { syncKeywords = true } = {}) => {
    if (signalId == null || restoringId != null) return;
    setRestoringId(signalId);
    setApiError(null);
    aiScenarioDemoService.getSignalDetail(signalId)
      .then(r => {
        const d = r.data || {};
        setCollectedEvent(d.event || null);
        setCollectedSignals(d.signals || []);
        setCollectedImpact(d.impact || null);
        // 품목 목록이 통째로 바뀌므로 이전 결과의 펼침 위치는 남기지 않는다
        setExpandedImpactIdx(null);
        setChannelsUsed(d.event?.channels_used || []);
        setCollectedPeriodStart(d.event?.collect_period_start ?? null);
        setCollectedPeriodEnd(d.event?.collect_period_end ?? null);
        if (syncKeywords) setSelectedKeywords(d.keywords || []);
        // 공급망 영향 분석은 run 응답 전체가 저장된 경우에만 복원(과거 기록은 수집 화면만)
        const a = d.analysis;
        setResult(a && (a.market_intelligence || a.metadata || a.affected_items) ? a : null);
        // 추가 컨텍스트는 기록마다 다르다 - 복원 대상의 값으로 교체(없으면 비움)
        setUserComment(a?.user_comment ?? "");
        setLiveStep("collected");
        setShowHistory(false);
      })
      .catch(e => setApiError(apiErrorText(e, t("mktIntelRestoreError"))))
      .finally(() => setRestoringId(null));
  };

  // 시장 신호 삭제 (연결된 분석 결과도 함께 제거) — 공통 Dialog 확인
  const [deletingSignalId, setDeletingSignalId] = useState(null);
  const [signalDeleteDialog, setSignalDeleteDialog] = useState({ open: false, signalId: null });
  const handleDeleteSignal = (signalId) => {
    if (signalId == null || deletingSignalId != null) return;
    setSignalDeleteDialog({ open: true, signalId });
  };
  const handleSignalDeleteConfirm = (action) => {
    const signalId = signalDeleteDialog.signalId;
    if (action !== "DELETE" || signalId == null) return;
    setDeletingSignalId(signalId);
    aiScenarioDemoService.deleteSignal(signalId)
      .then(() => setHistorySignals(prev => prev.filter(s => s.id !== signalId)))
      .catch(() => setApiError(t("mktIntelDeleteSignalFailed")))
      .finally(() => setDeletingSignalId(null));
  };

  // ── 산업군 선택 ─────────────────────────────────────────────────────────
  // 카탈로그에서 사라진 산업군이 복원되면 전체로 되돌린다(키워드 0건 화면 방지).
  const restoredIndustry = reduxViewState?.selectedIndustry;
  const [selectedIndustry, setSelectedIndustry] = useState(
    isKnownIndustry(restoredIndustry) ? restoredIndustry : ALL_INDUSTRY);

  // ── 다중 키워드 선택 ────────────────────────────────────────────────────
  const [selectedKeywords,  setSelectedKeywords]  = useState(reduxViewState?.selectedKeywords ?? []);
  const [customInput,       setCustomInput]        = useState("");
  const [showCustomInput,   setShowCustomInput]    = useState(false);

  // ── 채널 옵션 state ─────────────────────────────────────────────────────
  const [useTrends,        setUseTrends]        = useState(reduxViewState?.useTrends        ?? true);
  const [useNaver,         setUseNaver]         = useState(reduxViewState?.useNaver         ?? true);
  const [useGoogle,        setUseGoogle]        = useState(reduxViewState?.useGoogle        ?? true);
  const [useGdelt,         setUseGdelt]         = useState(reduxViewState?.useGdelt         ?? true);
  const [useScmPress,      setUseScmPress]      = useState(reduxViewState?.useScmPress      ?? true);
  const [useNewsdata,      setUseNewsdata]      = useState(reduxViewState?.useNewsdata      ?? true);
  // 조건부 채널(marketingChannels) on/off — { code: bool }. 미지정은 on
  const [condChannelOn,    setCondChannelOn]    = useState(reduxViewState?.condChannelOn    ?? {});
  const [showChannelOpts,  setShowChannelOpts]  = useState(false);
  // 언어 전환에 즉시 반응하도록 i18n.language 로 판정한다(getAgentLanguage 는 렌더와 무관)
  const agentLang = toAgentLanguage(i18n.language);
  const condChannels = eligibleChannels(agentLang, selectedIndustry);
  // 기본 채널의 언어 제한(marketingChannels.BUILTIN_CHANNEL_LANGUAGES) — 못 쓰는 채널은 선택지·전송에서 뺀다
  const isAvailable = (code) => isBuiltinAvailable(code, agentLang);
  const isCondOn = (code) => condChannelOn[code] ?? true;
  // 프리셋이 선택돼 있으면 저장된 날짜 대신 today 기준으로 다시 잡는다("최근 1개월"의 의미 유지)
  const initialPreset = reduxViewState?.collectPeriodPreset === undefined
    ? DEFAULT_COLLECT_DAYS : reduxViewState.collectPeriodPreset;
  const initialRange = initialPreset != null ? rangeFromPreset(initialPreset)
    : { start: reduxViewState?.collectPeriodStart || "", end: reduxViewState?.collectPeriodEnd || "" };
  const [collectPeriodPreset, setCollectPeriodPreset] = useState(initialPreset);
  const [collectPeriodStart, setCollectPeriodStart] = useState(initialRange.start);
  const [collectPeriodEnd, setCollectPeriodEnd] = useState(initialRange.end);
  const periodCustom = collectPeriodPreset == null;   // 직접 지정 모드
  // 날짜를 지웠거나 뒤집힌 구간으로는 수집을 시작하지 않는다
  const periodRangeValid = rangeDays(collectPeriodStart, collectPeriodEnd) > 0;
  // 직접 입력 파생어 추천 state
  const [kwSuggestions,    setKwSuggestions]    = useState([]);
  const [suggestLoading,   setSuggestLoading]   = useState(false);

  const resultRef = useRef(null);

  // ── Redux 상태 동기화 ────────────────────────────────────────────────────
  // collecting 중에 화면을 이탈한 경우 idle로 초기화 (진행 중 요청은 소멸)
  useEffect(() => {
    if (reduxViewState?.liveStep === "collecting") {
      setLiveStep("idle");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const reduxState = reduxUtil.createObjectState(viewName, {
      result,
      userComment,
      liveStep,
      collectedSignals,
      collectedEvent,
      channelsUsed,
      collectedPeriodStart,
      collectedPeriodEnd,
      collectPeriodPreset,
      collectPeriodStart,
      collectPeriodEnd,
      collectedImpact,
      selectedIndustry,
      selectedKeywords,
      useTrends,
      useNaver,
      useGoogle,
      useGdelt,
      useScmPress,
      useNewsdata,
      condChannelOn,
    });
    reduxDispatch(reduxUtil.updateViewState(reduxState));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, userComment, liveStep, collectedSignals, collectedEvent, channelsUsed, collectedPeriodStart, collectedPeriodEnd, collectPeriodPreset, collectPeriodStart, collectPeriodEnd, collectedImpact, selectedIndustry, selectedKeywords, useTrends, useNaver, useGoogle, useGdelt, useScmPress, useNewsdata, condChannelOn]);

  // ── 멀티 에이전트 수집·검증·분석 실행 ─────────────────────────────────────
  //   Supervisor 가 수집→정제→검증(Critic)→영향 분석(Impact)을 지휘. 저장까지 수행.
  //   SSE 로 도구 진행(tool_start/tool_done)을 받아 단계를 실시간 표시하고,
  //   스트림 실패 시 비스트림 엔드포인트로 자동 폴백한다.
  const handleAgentAnalyze = async () => {
    if (agentBusy || selectedKeywords.length === 0 || !periodRangeValid) return;
    // 라벨(t(labelKey))이 곧 검색어다 — 영어 UI면 영어로 검색·수집된다. 직접 입력은 입력값 그대로.
    const builtinOn = { naver: useNaver, google: useGoogle, trends: useTrends,
                        gdelt: useGdelt, scm: useScmPress, newsdata: useNewsdata };
    const builtins = Object.keys(builtinOn).filter(isAvailable);
    const channels = [...builtins.filter(c => builtinOn[c]),
                      ...condChannels.filter(c => isCondOn(c.code)).map(c => c.code)]
      .join(",")
      // 전부 끄면 기본 채널 전체 — 현재 언어에서 못 쓰는 채널은 빼야 '사용 채널' 기록이 사실과 맞는다
      || builtins.join(",");
    // 프리셋이 선택 중이면 실행 시점의 today 기준으로 구간을 다시 잡는다
    const range = collectPeriodPreset != null
      ? rangeFromPreset(collectPeriodPreset)
      : { start: collectPeriodStart, end: collectPeriodEnd };
    if (range.start !== collectPeriodStart) setCollectPeriodStart(range.start);
    if (range.end !== collectPeriodEnd) setCollectPeriodEnd(range.end);
    // 합치는 일은 서버가 한다 — 화면과 서버가 각자 합치면 어긋난다
    const collectOpts = { keywords: selectedLabels, channels, industry: selectedIndustry,
      matchTerms: matchTermsOf(selectedKeywords),
      periodStart: range.start, periodEnd: range.end };
    setAgentBusy(true); setAgentError(null);
    // 새 분석 시작 — 이전 복원/분석 화면을 클리어하고 진행 현황만 표시
    setCollectedSignals([]); setCollectedEvent(null); setCollectedImpact(null);
    setResult(null); setApiError(null); setChannelsUsed([]); setUserComment("");
    setContextFiles([]);   // 첨부 문서도 코멘트처럼 비운다 — 이전 키워드용 문서가 새 분석에 실려 가지 않게
    setCollectedPeriodStart(null); setCollectedPeriodEnd(null);
    setLiveStep("idle");
    setAgentSteps([{ tool: "_plan", label: t("mktIntelPlanningStep"), status: "running" }]);

    let gotResult = false;
    try {
      await aiScenarioDemoService.collectAnalyzeStream(collectOpts, {
        onEvent: (evt) => {
          if (evt.type === "tool_start") {
            setAgentSteps(prev => {
              // 계획 placeholder 는 첫 실제 단계 도착 시 완료 처리
              const base = prev.map(s => (s.tool === "_plan" && s.status === "running")
                ? { ...s, status: "done" } : s);
              return [...base, { tool: evt.tool, label: evt.label || evt.tool, status: "running" }];
            });
          } else if (evt.type === "tool_done") {
            setAgentSteps(prev => {
              const i = prev.findIndex(s => s.status === "running" && s.tool === evt.tool);
              if (i < 0) return prev;
              const next = [...prev];
              next[i] = { ...next[i], status: evt.status === "error" ? "error" : "done" };
              return next;
            });
          } else if (evt.type === "result") {
            gotResult = true;
            setAgentSteps(prev => prev.map(s => s.status === "running" ? { ...s, status: "done" } : s));
            setAgentBusy(false);            // 결과 도착 = 완료 — 스트림 잔여 종료를 기다리지 않음
            setHistorySignals([]);          // 저장된 신호가 생겼으므로 이전 기록 캐시 무효화
            // 완료 후에는 기존 분석 UI 로 전환 — 저장된 신호를 DB에서 복원해 표준 화면으로 표시
            if (evt.data?.signal_id != null) restoreFromDb(evt.data.signal_id, { syncKeywords: false });
          } else if (evt.type === "error") {
            setAgentError(evt.content || t("mktIntelAgentError"));
          }
        },
      });
      if (!gotResult && !agentError) {
        throw new Error(t("mktIntelStreamNoResult"));
      }
    } catch (e) {
      // SSE 미지원/프록시 문제 등 → 비스트림 폴백 (단계 표시 없이 결과만)
      try {
        setAgentSteps([{ tool: "_fallback", label: t("mktIntelFallbackStep"), status: "running" }]);
        const r = await aiScenarioDemoService.collectAnalyze(collectOpts);
        setAgentError(null);
        setHistorySignals([]);
        setAgentSteps(prev => prev.map(s => ({ ...s, status: "done" })));
        // 기존 분석 UI 로 전환
        if (r.data?.signal_id != null) restoreFromDb(r.data.signal_id, { syncKeywords: false });
      } catch (e2) {
        setAgentError(apiErrorText(e2, t("mktIntelAgentError")));
      }
    } finally {
      setAgentBusy(false);
    }
  };

  // 선택 키워드(kw) → 화면 표시용 라벨. 카탈로그 밖(직접 입력·이력 복원)은 입력값 그대로.
  const labelOf = (kw) => {
    const entry = keywordEntry(kw);
    return entry ? t(entry.labelKey) : kw;
  };
  const selectedLabels = selectedKeywords.map(labelOf);

  // 품목 매칭 전용 검색어 — 수집 시드(표시 언어 라벨)와 별개로 **영어만** 보낸다.
  //   사용자 언어는 시드가 이미 나르므로, 여기서 더할 값은 언어 중립인 것이어야 한다.
  //   ko 를 함께 보내면 "품목 마스터는 한국어"라는 전제를 코드에 박는 셈이 된다 —
  //   실측(sc_demo)에서 ko 적중 349건 중 253건이 한국어 품목명·산업명에 걸린 것이었고,
  //   ja/en 은 사전 경유가 거의 전부(225/226, 259/269)였다. 즉 ko 우위는 사전 품질이 아니라
  //   그 테넌트의 마스터 언어에서 온다. 해외 테넌트에선 재현되지 않는다.
  //   en 을 남기는 근거는 언어 무관하다 — ③ 임베딩 인덱스가 영어 개념구이고(피벗 실측 최고),
  //   형번·소재 코드(DDR5·OLED·MDF)는 어느 언어 사전에도 영문 그대로 들어간다.
  //   6개 언어가 모두 번들이라 표시 언어와 무관하게 같은 labelKey 의 en 값을 뽑을 수 있다.
  //   보내는 형태는 목록이 아니라 **{표시 라벨: 영어 표현} 대응표**다 — 백엔드가
  //   "이 검색어에는 영어가 있다/없다"를 알아야, 의미검색에서 영어가 있는 건 영어로 바꾸고
  //   자유 입력은 그대로 남길 수 있다. 목록으로 보내면 자유 입력이 조용히 빠진다.
  const matchTermsOf = (kwList) => {
    const out = {};
    kwList.forEach((kw) => {
      const entry = keywordEntry(kw);
      if (!entry) return;   // 직접 입력 — 대응하는 영어 라벨이 없으니 지어내지 않는다
      const label = t(entry.labelKey);
      const en = t(entry.labelKey, { lng: "en-US" });
      if (label && en) out[label] = en;
    });
    return out;
  };

  // 산업군 키워드 → 산업 무관 공통 키워드 → 이미 선택된 키워드(다른 산업군·직접 입력) 순.
  // 마지막 항을 빼면 "선택돼 있는데 칩이 화면에 없어 해제할 수 없는" 상태가 생긴다.
  const visibleKeywords = (() => {
    const seen = new Set();
    return [...industryKeywords(selectedIndustry),
            ...COMMON_KEYWORDS,
            ...selectedKeywords.map(keywordEntry).filter(Boolean)]
      .filter(e => !seen.has(e.kw) && seen.add(e.kw));
  })();

  const kwChipSx = (active) => ({
    fontWeight: active ? 700 : 500,
    fontSize: 11,
    cursor: agentBusy ? "not-allowed" : "pointer",
    bgcolor: active ? C.primary : C.neutralBg,
    color:   active ? "#fff"    : C.neutral,
    border: `1px solid ${active ? C.primary : C.border}`,
    "&:hover": !agentBusy
      ? { bgcolor: active ? C.primary : `${C.primary}15`, borderColor: C.primary }
      : {},
  });

  // 수집 기간 선택 — 키워드와 같이, 수집 중에는 조건을 바꿀 수 없다
  const handlePeriodSelect = (days) => {
    if (agentBusy) return;
    const r = rangeFromPreset(days);
    setCollectPeriodPreset(days);
    setCollectPeriodStart(r.start); setCollectPeriodEnd(r.end);
  };

  // 기간 세그먼트 — 직접 지정은 프리셋만 풀고 현재 구간을 그대로 편집 대상으로 넘긴다
  const handlePeriodModeChange = (v) => {
    if (agentBusy) return;
    if (v === PERIOD_CUSTOM) setCollectPeriodPreset(null);
    else handlePeriodSelect(v);
  };

  const handlePeriodDateChange = (which, dt) => {
    if (agentBusy) return;
    const v = periodDateToStr(dt);
    if (which === "start") setCollectPeriodStart(v); else setCollectPeriodEnd(v);
  };

  // 프리셋 칩 토글 (멀티셀렉트)
  const handlePresetToggle = (kw) => {
    if (agentBusy) return;
    setSelectedKeywords(prev =>
      prev.includes(kw) ? prev.filter(k => k !== kw) : [...prev, kw]
    );
  };

  // 선택 키워드 제거
  const handleRemoveKeyword = (kw) => {
    setSelectedKeywords(prev => prev.filter(k => k !== kw));
  };

  // 선택 키워드 전체 해제
  const handleClearKeywords = () => {
    if (agentBusy) return;
    setSelectedKeywords([]);
  };

  // 직접 입력 → 선택 키워드에 추가 + 파생어 추천 조회
  const handleAddCustom = () => {
    const kw = customInput.trim();
    if (!kw || selectedKeywords.includes(kw)) return;
    setSelectedKeywords(prev => [...prev, kw]);
    setCustomInput("");
    // 직접 입력 키워드는 파생어가 없으므로 LLM 추천을 조회해 사용자가 추가하도록 함
    setSuggestLoading(true);
    setKwSuggestions([]);
    aiScenarioDemoService.suggestKeywords(kw, 5)
      .then(r => setKwSuggestions(r.data?.suggestions || []))
      .catch(() => setKwSuggestions([]))
      .finally(() => setSuggestLoading(false));
  };

  // 추천 파생어 칩 클릭 → 선택 키워드에 추가
  const handleAddSuggestion = (kw) => {
    if (!kw || selectedKeywords.includes(kw)) return;
    setSelectedKeywords(prev => [...prev, kw]);
    setKwSuggestions(prev => prev.filter(k => k !== kw));
  };

  // 분석 실행
  const handleAnalyze = () => {
    if (analyzing) return;
    if (!collectedEvent) return;
    setAnalyzing(true); setResult(null); setApiError(null);

    aiScenarioDemoService.runDemoWithEvent(collectedEvent, null, userComment, contextFiles)
      .then(r => {
        setResult(r.data);
        setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
      })
      .catch(e => setApiError(apiErrorText(e, t("mktIntelAnalysisError"))))
      .finally(() => setAnalyzing(false));
  };

  // AI 추천 허브로 보내기
  const handleSendToRecommendation = (r) => {
    const analysisId = r?.metadata?.signal_impact_id || r?.demo_request_id || null;
    // AI_RECOMMENDATION 뷰스테이트에 highlightAnalysisId 저장
    const recState = reduxUtil.createObjectState("AI_RECOMMENDATION", { highlightAnalysisId: analysisId });
    reduxDispatch(reduxUtil.updateViewState(recState));
    // AI 추천 탭으로 이동
    reduxDispatch(updateHistoryState("AI_RECOMMENDATION"));
  };


  return (
    // 폭 상한을 두지 않는다 — 상한이 있으면 카드가 창·사이드바 폭 변화를 못 따라가고,
    // 자기 margin 을 가진 직계 자식(Grid container)이 가운데 정렬을 이겨 혼자 왼쪽으로 밀린다.
    // scrollbarGutter — 스크롤바가 오갈 때 폭이 15px 흔들려 본문이 다시 줄바꿈되는 것을 막는다.
    <Box sx={{
      p: "16px 20px 40px", bgcolor: C.bg, height: "100%", overflow: "auto",
      scrollbarGutter: "stable", boxSizing: "border-box",
    }}>


      {/* ── 헤더 ── */}
      <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
        <Box sx={{ bgcolor: C.primary, borderRadius: 1.5, p: "6px 9px",
          display: "flex", alignItems: "center" }}>
          <CampaignIcon sx={{ color: "#fff", fontSize: 20 }} />
        </Box>
        {/* 제목 영역이 먼저 줄어들도록 minWidth:0 — 버튼이 찌그러지지 않게 한다.
            제목·부제는 말줄임 처리해 창 폭이 변해도 헤더 높이가 고정된다. */}
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: FONT.H1, fontWeight: 800, lineHeight: 1.2, color: C.text1 }}>
            {t("mktIntelTitle")}
          </Typography>
          <Typography noWrap sx={{ fontSize: FONT.BODY, color: C.text2 }}>
            {t("mktIntelSubtitle")}
          </Typography>
        </Box>
        {/* 이전 기록 버튼 (+ 관리자 전용 임베딩 인덱스 상태/구축) */}
        {/* flexShrink:0 — 폭이 좁아져도 버튼이 줄바꿈·축소되지 않는다 (높이 요동 방지) */}
        <Box sx={{ ml: "auto !important", display: "flex", alignItems: "center", gap: 1,
          flexShrink: 0, "& .MuiButton-root": { whiteSpace: "nowrap", flexShrink: 0 } }}>
          {isEmbedAdmin && (() => {
            // ── 품목 매핑 데이터 (키워드 분류 + 임베딩) — 원버튼 통합 구축 ──
            const cCnt = clsStatus?.classified_count ?? null;
            const cUn  = clsStatus?.unclassified_count ?? 0;
            const emb  = embedStatus?.embedded ?? null;
            const tot  = embedStatus?.total_items ?? null;
            const busy = clsBusy || embedBusy;
            const never = (cCnt === 0) && (emb === 0);
            const stale = (Number(cUn) > 0) || (emb != null && tot != null && emb < tot);
            const label = (cCnt == null && emb == null) ? t("mktIntelMappingUnknown")
              : never ? t("mktIntelMappingNotBuilt")
              : t("mktIntelMappingStatus", {
                  cls: Number(cCnt || 0).toLocaleString(),
                  emb: Number(emb || 0).toLocaleString(),
                  tot: Number(tot || 0).toLocaleString(),
                });
            return (
              <>
                <Chip size="small" label={label}
                  sx={{ height: 22, fontSize: FONT.CAPTION,
                    bgcolor: never || cCnt == null ? C.warningBg : stale ? C.warningBg : C.successBg,
                    color:   never || cCnt == null ? C.warning   : stale ? C.warning   : C.success }} />
                <Button size="small" variant="outlined" disabled={busy}
                  startIcon={busy ? <CircularProgress size={12} /> : null}
                  onClick={handleMappingBuild}
                  sx={{ fontSize: FONT.CAPTION, fontWeight: 600, borderRadius: 2 }}>
                  {busy ? t("mktIntelMappingBuilding") : never ? t("mktIntelMappingBuildBtn") : t("mktIntelMappingRefreshBtn")}
                </Button>
              </>
            );
          })()}
          <Button size="small" variant={showHistory ? "contained" : "outlined"}
            startIcon={historyLoading ? null : <HistoryIcon sx={{ fontSize: 15 }} />}
            onClick={loadHistory}
            disabled={historyLoading}
            sx={{ fontSize: FONT.CAPTION, fontWeight: 600, borderRadius: 2,
              bgcolor: showHistory ? C.primary : undefined,
              "&:hover": { bgcolor: showHistory ? "#0C447C" : undefined } }}>
            {historyLoading ? t("bfLoadingEllipsis") : showHistory ? t("mktIntelHistoryClose") : t("mktIntelHistoryBtn")}
          </Button>
        </Box>
      </Stack>

      {/* ── 이전 기록 패널 ── */}
      {showHistory && (
        <Card variant="outlined" sx={{ borderRadius: 2, mb: 2, border: `1px solid ${C.primary}30` }}>
          <CardContent sx={{ p: "12px 16px !important" }}>
            <Typography variant="subtitle2" fontWeight={700} color={C.primary} mb={1}>
              {t("mktIntelHistoryPanelTitle", { count: historySignals.length })}
            </Typography>
            {historySignals.length === 0 ? (
              <Typography variant="body2" color="text.disabled">{t("mktIntelHistoryEmpty")}</Typography>
            ) : (
              <Stack spacing={0.6}>
                {historySignals.map((sig, idx) => {
                  const chg = parseFloat(sig.demand_impact_pct || 0);
                  const up  = chg > 0;
                  const statusColor = {
                    ANALYZED: C.success, PENDING: C.warning,
                    APPLIED: C.primary, DISMISSED: C.neutral
                  }[sig.status_cd] || C.neutral;
                  const isRestoring = restoringId != null && String(restoringId) === String(sig.id);
                  return (
                    <Stack key={sig.id || idx} direction="row" alignItems="center"
                      onClick={() => restoreFromDb(sig.id)}
                      spacing={1} sx={{ py: "4px", borderBottom: `1px solid ${C.border}`,
                        "&:last-child": { borderBottom: "none" },
                        cursor: sig.id != null ? "pointer" : "default", borderRadius: 1,
                        opacity: (restoringId != null && !isRestoring) ? 0.5 : 1,
                        "&:hover": { bgcolor: `${C.primary}0A` } }}>
                      <Chip label={sig.status_cd || "PENDING"} size="small"
                        sx={{ height: 16, fontSize: 9, fontWeight: 700,
                          bgcolor: `${statusColor}20`, color: statusColor, minWidth: 60 }} />
                      <Typography variant="caption" fontWeight={600} sx={{ flex: 1, minWidth: 0,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {sig.title || sig.keyword}
                      </Typography>
                      {sig.id != null && (
                        <Tooltip title={t("mktIntelRestoreTooltip")}>
                          {isRestoring
                            ? <CircularProgress size={13} sx={{ color: C.primary, flexShrink: 0 }} />
                            : <RestoreIcon sx={{ fontSize: 14, color: C.primary, flexShrink: 0 }} />}
                        </Tooltip>
                      )}
                      <Typography variant="caption" fontWeight={700}
                        sx={{ color: up ? C.success : C.danger, flexShrink: 0 }}>
                        {t("bfDemandPctLabel", { sign: up ? "+" : "", pct: chg.toFixed(0) })}
                      </Typography>
                      <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>
                        {(sig.created_ts || "").slice(0, 10)}
                      </Typography>
                      {sig.id != null && (
                        <Tooltip title={t("mktIntelDeleteSignalTooltip")}>
                          <IconButton size="small" disabled={deletingSignalId === sig.id}
                            onClick={(e) => { e.stopPropagation(); handleDeleteSignal(sig.id); }}
                            sx={{ flexShrink: 0, p: "2px", color: C.danger,
                              "&:hover": { bgcolor: `${C.danger}15` } }}>
                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── 키워드 선택 영역 — 항상 노출 ── */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 2, borderColor: C.border }}>
        {/* 시나리오 플래닝 회색 헤더 바 */}
        <Box sx={{ p: "10px 15px", bgcolor: C.neutralBg, borderBottom: `0.5px solid ${C.border}`,
          display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: FONT.TITLE, fontWeight: 700, color: C.text1 }}>{t("mktIntelCollectSettingsTitle")}</Typography>
          <Typography sx={{ fontSize: FONT.BODY, color: C.text3 }}>{t("mktIntelCollectSettingsSub")}</Typography>
        </Box>
        <CardContent sx={{ p: "12px 16px !important" }}>

              {/* 수집 조건 — 라벨 열과 칩 열을 구분선 없이 정렬과 서식만으로 나눈다 */}
              <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr",
                columnGap: 1.5, rowGap: 1.2, alignItems: "start" }}>

                {/* 산업군 — 하나만 고르는 축이라 칩이 아니라 연결 세그먼트로 둔다 */}
                <Box sx={FIELD_LABEL_SX}>
                  <CategoryIcon sx={FIELD_LABEL_ICON_SX} />
                  <Typography sx={FIELD_LABEL_TEXT_SX}>{t("mktIntelIndustryLabel")}</Typography>
                </Box>
                <AiSegmentedControl
                  accent={C.primary}
                  value={selectedIndustry}
                  onChange={(v) => { if (!agentBusy) setSelectedIndustry(v); }}
                  options={[{ value: ALL_INDUSTRY, label: t("all") },
                    ...INDUSTRIES.map(({ code, labelKey }) => ({ value: code, label: t(labelKey) }))]}
                  sx={{ justifySelf: "start" }}
                />

                {/* 산업 키워드 — 산업군에 따라 앞부분이 바뀐다. 공통 키워드는 뒤에 붙어 항상 남는다. */}
                <Box sx={FIELD_LABEL_SX}>
                  <SearchIcon sx={FIELD_LABEL_ICON_SX} />
                  <Typography sx={FIELD_LABEL_TEXT_SX}>{t("mktIntelIndustryKeywords")}</Typography>
                </Box>
                <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
                  {visibleKeywords.map(({ labelKey, kw }) => {
                    const active = selectedKeywords.includes(kw);
                    return (
                      <Tooltip
                        key={kw}
                        title={
                          <Typography variant="caption" display="block">
                            {t("mktIntelPresetTooltip", { label: t(labelKey) })}
                          </Typography>
                        }
                        placement="bottom"
                        arrow
                      >
                        <Chip
                          label={t(labelKey)}
                          size="small"
                          onClick={() => handlePresetToggle(kw)}
                          sx={kwChipSx(active)}
                        />
                      </Tooltip>
                    );
                  })}
                  {/* 직접 입력 토글 버튼 */}
                  <Chip
                    label={showCustomInput ? t("mktIntelCustomInputClose") : t("mktIntelCustomInputOpen")}
                    size="small"
                    onClick={() => setShowCustomInput(v => !v)}
                    variant="outlined"
                    sx={{
                      fontWeight: 600, fontSize: 11,
                      cursor: "pointer",
                      borderColor: showCustomInput ? C.danger : C.primary,
                      color: showCustomInput ? C.danger : C.primary,
                      "&:hover": { bgcolor: showCustomInput ? `${C.danger}08` : `${C.primary}08` },
                    }}
                  />
                  {/* 채널 설정 토글 */}
                  <Chip
                    icon={<TuneIcon sx={{ fontSize: "12px !important" }} />}
                    label={t("mktIntelChannelChip")}
                    size="small"
                    onClick={() => setShowChannelOpts(v => !v)}
                    variant={showChannelOpts ? "filled" : "outlined"}
                    sx={{
                      fontWeight: 600, fontSize: 11, cursor: "pointer",
                      bgcolor: showChannelOpts ? `${C.purple}15` : "transparent",
                      borderColor: C.purple, color: C.purple,
                      "& .MuiChip-icon": { color: C.purple },
                      "&:hover": { bgcolor: `${C.purple}10` },
                    }}
                  />
                </Stack>

                {/* 채널 옵션 패널 — 칩 열에 맞춰 시선의 기점을 하나로 유지한다 */}
                {showChannelOpts && (
                <Collapse in appear timeout={150} sx={{ gridColumn: "2" }}>
                  <Box sx={{ p: "8px 12px", borderRadius: 1.5,
                    bgcolor: `${C.purple}06`, border: `1px solid ${C.purple}20` }}>
                    <Typography variant="caption" fontWeight={700} color={C.purple}
                      sx={{ display: "block", mb: 0.8 }}>
                      {t("mktIntelChannelSettings")}
                    </Typography>
                    {/* 채널 on/off 토글 */}
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap mb={0.6}>
                      {/* 기본 채널 — 현재 언어에서 쓸 수 있는 것만 노출 (예: 네이버는 ko 전용) */}
                      {isAvailable("naver") && (
                        <FormControlLabel
                          control={
                            <AiStyledSwitch accent="#03c75a" checked={useNaver}
                              onChange={e => setUseNaver(e.target.checked)}
                              disabled={agentBusy} sx={{ mr: 0.8 }}
                            />
                          }
                          label={
                            <Typography variant="caption" fontWeight={600} sx={{ color: "#03c75a" }}>
                              {t("mktIntelChannelNaver")}
                            </Typography>
                          }
                          sx={{ m: 0 }}
                        />
                      )}
                      {isAvailable("google") && (
                        <FormControlLabel
                          control={
                            <AiStyledSwitch accent="#ea4335" checked={useGoogle}
                              onChange={e => setUseGoogle(e.target.checked)}
                              disabled={agentBusy} sx={{ mr: 0.8 }}
                            />
                          }
                          label={
                            <Typography variant="caption" fontWeight={600} sx={{ color: "#ea4335" }}>
                              {t("mktIntelChannelGoogle")}
                            </Typography>
                          }
                          sx={{ m: 0 }}
                        />
                      )}
                      {isAvailable("trends") && (
                        <FormControlLabel
                          control={
                            <AiStyledSwitch accent={C.purple} checked={useTrends}
                              onChange={e => setUseTrends(e.target.checked)}
                              disabled={agentBusy} sx={{ mr: 0.8 }}
                            />
                          }
                          label={
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <ShowChartIcon sx={{ fontSize: 13, color: C.purple }} />
                              <Typography variant="caption" fontWeight={600} color={C.purple}>
                                Google Trends
                              </Typography>
                            </Stack>
                          }
                          sx={{ m: 0 }}
                        />
                      )}
                      {isAvailable("gdelt") && (
                        <FormControlLabel
                          control={
                            <AiStyledSwitch accent="#1a73e8" checked={useGdelt}
                              onChange={e => setUseGdelt(e.target.checked)}
                              disabled={agentBusy} sx={{ mr: 0.8 }}
                            />
                          }
                          label={
                            <Typography variant="caption" fontWeight={600} sx={{ color: "#1a73e8" }}>
                              {t("mktIntelChannelGdelt")}
                            </Typography>
                          }
                          sx={{ m: 0 }}
                        />
                      )}
                      {isAvailable("scm") && (
                        <FormControlLabel
                          control={
                            <AiStyledSwitch accent="#0f766e" checked={useScmPress}
                              onChange={e => setUseScmPress(e.target.checked)}
                              disabled={agentBusy} sx={{ mr: 0.8 }}
                            />
                          }
                          label={
                            <Typography variant="caption" fontWeight={600} sx={{ color: "#0f766e" }}>
                              {t("mktIntelChannelScmPress")}
                            </Typography>
                          }
                          sx={{ m: 0 }}
                        />
                      )}
                      {isAvailable("newsdata") && (
                        <FormControlLabel
                          control={
                            <AiStyledSwitch accent="#c2410c" checked={useNewsdata}
                              onChange={e => setUseNewsdata(e.target.checked)}
                              disabled={agentBusy} sx={{ mr: 0.8 }}
                            />
                          }
                          label={
                            <Typography variant="caption" fontWeight={600} sx={{ color: "#c2410c" }}>
                              {t("mktIntelChannelNewsdata")}
                            </Typography>
                          }
                          sx={{ m: 0 }}
                        />
                      )}
                      {/* 조건부 채널 — 현재 언어·산업군 조건을 만족할 때만 노출 */}
                      {condChannels.map(c => (
                        <FormControlLabel key={c.code}
                          control={
                            <AiStyledSwitch accent={c.color} checked={isCondOn(c.code)}
                              onChange={e => setCondChannelOn(prev => ({ ...prev, [c.code]: e.target.checked }))}
                              disabled={agentBusy} sx={{ mr: 0.8 }}
                            />
                          }
                          label={
                            <Typography variant="caption" fontWeight={600} sx={{ color: c.color }}>
                              {t(c.labelKey)}
                            </Typography>
                          }
                          sx={{ m: 0 }}
                        />
                      ))}
                    </Stack>
                  </Box>
                </Collapse>
                )}

                {/* 직접 입력 필드 — 토글 시 노출 */}
                {showCustomInput && (
                <Collapse in appear timeout={150} sx={{ gridColumn: "2" }}>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      fullWidth size="small"
                      placeholder={t("mktIntelCustomInputPlaceholder")}
                      value={customInput}
                      onChange={e => setCustomInput(e.target.value)}
                      onKeyDown={e => {
                        // 한글 IME 조합 확정 Enter 무시 — 미가드 시 마지막 음절이 별도 키워드로
                        // 한 번 더 추가된다 ("자전거" 입력 → "자전거"+"거" 칩 2개 버그).
                        if (e.key === "Enter" && !e.shiftKey
                            && !e.nativeEvent.isComposing && e.keyCode !== 229) {
                          e.preventDefault(); handleAddCustom();
                        }
                      }}
                      disabled={agentBusy}
                      InputProps={{ sx: { bgcolor: "#fff", fontSize: 13 } }}
                      sx={{ "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: `${C.primary}40` },
                        "&.Mui-focused fieldset": { borderColor: C.primary },
                      }}}
                    />
                    <Button
                      variant="outlined" size="small"
                      onClick={handleAddCustom}
                      disabled={agentBusy || !customInput.trim()}
                      sx={{ fontWeight: 700, borderRadius: 1.5, whiteSpace: "nowrap", px: 2,
                        borderColor: C.primary, color: C.primary }}>
                      {"+ "}{t("add")}
                    </Button>
                  </Stack>

                  {/* 직접 입력 키워드에 대한 파생어 추천 */}
                  {(suggestLoading || kwSuggestions.length > 0) && (
                    <Stack direction="row" alignItems="center" spacing={0.6} mt={1}
                      flexWrap="wrap" useFlexGap>
                      <Typography variant="caption" sx={{ flexShrink: 0, fontSize: 10, fontWeight: 600, color: C.primary }}>
                        {t("mktIntelSuggestedKeywords")}
                      </Typography>
                      {suggestLoading ? (
                        <Stack direction="row" spacing={0.6} alignItems="center">
                          <CircularProgress size={11} sx={{ color: C.primary }} />
                          <Typography variant="caption" color="text.disabled" fontSize={10}>
                            {t("mktIntelSuggestLoading")}
                          </Typography>
                        </Stack>
                      ) : (
                        kwSuggestions.map(v => (
                          <Chip key={v} label={`+ ${v}`} size="small"
                            onClick={() => handleAddSuggestion(v)}
                            disabled={agentBusy}
                            sx={{ height: 20, fontSize: 10.5, fontWeight: 600, cursor: "pointer",
                              bgcolor: C.primaryBg, color: C.primary,
                              border: `1px dashed ${C.primary}60`,
                              "&:hover": { bgcolor: `${C.primary}18` } }} />
                        ))
                      )}
                    </Stack>
                  )}
                </Collapse>
                )}

                {/* 수집 기간 — 칩이 규칙, 날짜가 결과다. 직접 지정을 고르기 전엔 날짜를 잠근다 */}
                <Box sx={FIELD_LABEL_SX}>
                  <DateRangeIcon sx={FIELD_LABEL_ICON_SX} />
                  <Typography sx={FIELD_LABEL_TEXT_SX}>{t("mktIntelCollectPeriodLabel")}</Typography>
                </Box>
                <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap
                  alignItems="center" sx={{ minWidth: 0 }}>
                  <AiSegmentedControl
                    accent={C.primary}
                    value={periodCustom ? PERIOD_CUSTOM : collectPeriodPreset}
                    onChange={handlePeriodModeChange}
                    options={[
                      ...COLLECT_PERIODS.map(({ days, labelKey }) => ({ value: days, label: t(labelKey) })),
                      { value: PERIOD_CUSTOM, label: t("mktIntelPeriodCustom") },
                    ]}
                    sx={{ flexShrink: 0 }}
                  />
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      format="yyyy-MM-dd"
                      value={periodStrToDate(collectPeriodStart)}
                      disabled={agentBusy || !periodCustom}
                      minDate={shiftDays(todayDate(), -(MAX_RANGE_DAYS - 1))}
                      maxDate={periodStrToDate(collectPeriodEnd) || todayDate()}
                      onChange={(dt) => handlePeriodDateChange("start", dt)}
                      slotProps={{ textField: {
                        size: "small", placeholder: "yyyy-MM-dd",
                        sx: periodCustom ? DP_SX : DP_SX_LOCKED,
                        error: periodCustom && !periodRangeValid,
                      } }}
                    />
                    <Typography sx={{ fontSize: 11, mx: 0.2,
                      color: periodCustom ? C.text3 : C.border }}>~</Typography>
                    <DatePicker
                      format="yyyy-MM-dd"
                      value={periodStrToDate(collectPeriodEnd)}
                      disabled={agentBusy || !periodCustom}
                      minDate={periodStrToDate(collectPeriodStart) || undefined}
                      maxDate={todayDate()}
                      onChange={(dt) => handlePeriodDateChange("end", dt)}
                      slotProps={{ textField: {
                        size: "small", placeholder: "yyyy-MM-dd",
                        sx: periodCustom ? DP_SX : DP_SX_LOCKED,
                        error: periodCustom && !periodRangeValid,
                      } }}
                    />
                  </LocalizationProvider>
                  {periodCustom && !periodRangeValid && (
                    <Typography sx={{ fontSize: 10.5, color: C.danger }}>
                      {t("mpPeriodReversedWarning")}
                    </Typography>
                  )}
                </Stack>

                {/* 선택된 키워드 — 입력이 아니라 결과/상태다. 옅은 배경으로 입력 영역과 분리한다 */}
                {selectedKeywords.length > 0 && (
                  <Box sx={{ gridColumn: "1 / -1", mt: 0.4, p: "8px 10px", borderRadius: 1.5,
                    bgcolor: C.neutralBg }}>
                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                      <Typography sx={{ ...FIELD_LABEL_TEXT_SX, color: C.text2, fontWeight: 700 }}>
                        {t("selected")}
                      </Typography>
                      {selectedKeywords.map(kw => (
                        <Chip
                          key={kw}
                          label={labelOf(kw)}
                          size="small"
                          onDelete={() => handleRemoveKeyword(kw)}
                          disabled={agentBusy}
                          sx={{
                            height: 26, fontWeight: 700, fontSize: 11,
                            bgcolor: "#fff", color: C.primary,
                            border: `1px solid ${C.primary}50`,
                            "& .MuiChip-deleteIcon": {
                              // 히트 영역 24px 확보 — 아이콘만 키우면 누르기 어렵다
                              color: C.primary, fontSize: 18, width: 24, height: 24,
                              padding: "3px", margin: "0 2px 0 -2px",
                              "&:hover": { color: C.danger },
                            },
                          }}
                        />
                      ))}
                      {/* 전체 해제 — 칩을 하나씩 지우지 않아도 되게 한다 */}
                      <Button
                        size="small" variant="text" onClick={handleClearKeywords}
                        disabled={agentBusy}
                        sx={{ minWidth: 0, px: 1, height: 24,
                          fontSize: 11, fontWeight: 600, color: C.text3,
                          "&:hover": { color: C.danger, bgcolor: `${C.danger}0A` } }}>
                        {t("clearAll")}
                      </Button>
                    </Stack>
                    {/* 파생 검색어는 내부적으로만 확장·수집되며 화면에는 노출하지 않습니다. */}
                  </Box>
                )}
              </Box>

              {/* 실행 — 대기 중엔 버튼, 실행 중엔 같은 자리에 진행 현황 표시 */}
              {!agentBusy && (
                <Box sx={{ mt: 1.4, textAlign: "center" }}>
                  <Button
                    variant="contained" size="medium"
                    startIcon={<AutoAwesomeIcon />}
                    disabled={selectedKeywords.length === 0 || !periodRangeValid}
                    onClick={handleAgentAnalyze}
                    sx={{
                      fontWeight: 700, borderRadius: 2, width: 380, maxWidth: "100%",
                      height: 42,   // 라벨이 길어져도(선택 키워드 다수·영문) 높이 고정 — 아래 말줄임과 세트
                      background: selectedKeywords.length > 0
                        ? `linear-gradient(135deg, ${C.primary}, #7c3aed)` : undefined,
                    }}>
                    {/* 버튼 텍스트 한 줄 고정 — 줄바꿈되면 창 폭이 바뀔 때마다 버튼 높이가
                        요동친다(선택 키워드가 길수록 심함). 넘치는 부분은 말줄임(...) 처리. */}
                    <Box component="span" sx={{
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0,
                    }}>
                      {selectedKeywords.length > 0
                        ? t("mktIntelRunAgentBtn")
                        : t("mktIntelSelectKeywordFirst")}
                    </Box>
                  </Button>
                  <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3, mt: 0.5 }}>
                    {t("mktIntelPipelineHint")}
                  </Typography>
                </Box>
              )}
              {agentBusy && agentSteps.length > 0 && (
                <Box sx={{ mt: 1.4, mx: "auto", maxWidth: 520, textAlign: "left",
                  p: "12px 16px", bgcolor: C.primaryBg, borderRadius: 2,
                  border: `1px solid ${C.primary}20` }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <CircularProgress size={14} />
                    <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700 }}>
                      {t("mktIntelAgentProgress")}
                    </Typography>
                  </Stack>
                  <Stack spacing={0.4}>
                    {agentSteps.map((s, i) => (
                      <Stack key={i} direction="row" alignItems="center" spacing={0.8}>
                        {s.status === "running"
                          ? <CircularProgress size={12} sx={{ flexShrink: 0 }} />
                          : s.status === "error"
                          ? <ErrorOutlineIcon sx={{ fontSize: 14, color: C.warning, flexShrink: 0 }} />
                          : <CheckCircleIcon sx={{ fontSize: 14, color: C.success, flexShrink: 0 }} />}
                        <Typography sx={{
                          fontSize: FONT.BODY,
                          color: s.status === "running" ? C.text1 : C.text3,
                          fontWeight: s.status === "running" ? 600 : 400,
                        }}>
                          {s.label}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              )}

              {apiError && liveStep === "idle" && (
                <Alert severity="error" sx={{ mt: 1.2, py: 0.5, borderRadius: 1.5 }}
                  onClose={() => setApiError(null)}>{apiError}</Alert>
              )}
            </CardContent>
          </Card>


          {/* ── 멀티 에이전트 분석 결과 — 판단 로그(왜 재수집/탈락했는지) 포함 ── */}
          {agentError && (
            <Alert severity="error" onClose={() => setAgentError(null)} sx={{ borderRadius: 2 }}>
              {agentError}
            </Alert>
          )}
          {/* 완료 직후 요약 카드는 노출하지 않는다 — 반짝임 방지.
              결과는 restoreFromDb 가 로드하는 아래 "수집 완료" 화면에 정리되어 표시된다.
              복원 로딩 동안은 빈 화면 대신 로딩 표시를 둔다. */}
          {restoringId != null && liveStep !== "collected" && (
            <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: "#fff" }}>
              <CardContent sx={{ p: "20px !important", textAlign: "center" }}>
                <CircularProgress size={18} sx={{ color: C.primary, mb: 1 }} />
                <Typography sx={{ fontSize: FONT.SUB, color: C.text2 }}>
                  {t("mktIntelPreparingResult")}
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* 수집 완료 */}
          {liveStep === "collected" && (
            <>
              {/* 2열: 신호 목록 | AI 요약 + 분석 버튼 */}
              <Grid container spacing={2} alignItems="stretch">
                {/* 좌: 수집된 신호 목록 */}
                <Grid item xs={12} md={4} sx={{ display: "flex", flexDirection: "column" }}>
                  <Card variant="outlined" sx={{ borderRadius: 2, flex: 1, display: "flex", flexDirection: "column" }}>
                    <CardContent sx={{ p: "12px 14px !important", flex: 1, display: "flex", flexDirection: "column" }}>
                      <Stack direction="row" alignItems="center" spacing={0.8} mb={0.8} flexWrap="wrap" useFlexGap>
                        <NewspaperIcon sx={{ fontSize: 16, color: C.primary }} />
                        <Typography variant="subtitle2" fontWeight={700} noWrap>{t("mktIntelCollectedSignals")}</Typography>
                        <Stack direction="row" alignItems="center" spacing={0.6} sx={{ ml: "auto !important" }}>
                          {collectedPeriodStart && collectedPeriodEnd && (
                            <Chip
                              icon={<DateRangeIcon sx={{ fontSize: "11px !important" }} />}
                              label={rangeChipLabel(collectedPeriodStart, collectedPeriodEnd)}
                              size="small"
                              variant="outlined"
                              sx={{ height: 18, fontSize: 10, fontWeight: 600,
                                color: C.text3, borderColor: C.border,
                                "& .MuiChip-icon": { color: C.text3, ml: "4px" } }}
                            />
                          )}
                          <Chip label={t("countCases", { count: collectedSignals.length })} size="small"
                            sx={{ height: 18, fontSize: 10, fontWeight: 700,
                              bgcolor: C.primaryBg, color: C.primary }} />
                        </Stack>
                      </Stack>
                      {/* 채널 사용 현황 */}
                      {channelsUsed.length > 0 && (
                        <Stack direction="row" spacing={0.5} mb={0.8} flexWrap="wrap" useFlexGap>
                          {channelsUsed.map(ch => {
                            const META = {
                              naver:    { label: t("mktIntelChNaverBrowser"),  color: "#03c75a" },
                              google:   { label: t("mktIntelChannelGoogle"),   color: "#ea4335" },
                              trends:   { label: "Trends",         color: C.purple },
                              gdelt:    { label: "GDELT",          color: "#1a73e8" },
                              scm:      { label: t("mktIntelChannelScmPress"), color: "#0f766e" },
                              newsdata: { label: "NewsData",       color: "#c2410c" },
                              browser:  { label: "Browser",        color: C.teal },
                              rss:      { label: "RSS",            color: C.neutral },
                              brave:    { label: "Brave",          color: "#fb923c" },
                              http:     { label: "HTTP",           color: "#94a3b8" },
                            };
                            const cond = channelByCode(ch);
                            const m = META[ch]
                              || (cond ? { label: t(cond.labelKey), color: cond.color } : { label: ch, color: C.neutral });
                            return (
                              <Chip key={ch} label={m.label} size="small"
                                sx={{ height: 16, fontSize: 9, fontWeight: 700,
                                  bgcolor: `${m.color}15`, color: m.color,
                                  border: `1px solid ${m.color}30` }} />
                            );
                          })}
                        </Stack>
                      )}
                      <Typography variant="caption" color="text.disabled" mb={1} display="block">
                        {t("mktIntelClickForDetail")}
                      </Typography>
                      {/* 목록 높이는 오른쪽 열이 정한 카드 높이에 고정(absolute-fill) — 내용이 넘칠 때만 그 지점에서 스크롤.
                          고정 maxHeight 금지(카드는 늘어나는데 목록만 잘림), flex-grow 단독 금지(목록 내용이 열 높이를 밀어냄) */}
                      <Box sx={{ flex: 1, minHeight: 0, position: "relative" }}>
                        <Box sx={{ position: "absolute", inset: 0, overflowY: "auto", pr: 0.5,
                          "&::-webkit-scrollbar": { width: 4 },
                          "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
                          "&::-webkit-scrollbar-thumb": { bgcolor: `${C.neutral}40`, borderRadius: 2 } }}>
                          {collectedSignals.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
                              {t("mktIntelNoSignals")}
                            </Typography>
                          ) : collectedSignals.map((sig, idx) => (
                            <SignalReadRow key={idx} signal={sig} />
                          ))}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* 우: AI 요약 + 코멘트 + 분석 버튼 */}
                <Grid item xs={12} md={8} sx={{ display: "flex", flexDirection: "column" }}>
                  <Stack spacing={1.5} sx={{ flex: 1 }}>
                    {collectedEvent && (
                      <Card variant="outlined" sx={{ borderRadius: 2,
                        border: `1.5px solid ${C.primary}30`, bgcolor: C.primaryBg }}>
                        <CardContent sx={{ p: "12px 16px !important" }}>
                          <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap alignItems="center" mb={1}>
                            <Chip size="small" label={collectedEvent.type?.replace("_", " ")}
                              sx={{ fontSize: 10, bgcolor: C.primary, color: "#fff", fontWeight: 700 }} />
                            {showEventMetrics && (
                              <Chip size="small"
                                label={t("bfDemandPctLabel", { sign: collectedEvent.demand_impact_pct > 0 ? "+" : "", pct: collectedEvent.demand_impact_pct?.toFixed(0) })}
                                sx={{ fontSize: 10, fontWeight: 700,
                                  bgcolor: collectedEvent.demand_impact_pct > 0 ? C.successBg : C.dangerBg,
                                  color:   collectedEvent.demand_impact_pct > 0 ? C.success   : C.danger }} />
                            )}
                            <Chip size="small"
                              label={t("mktIntelConfidencePct", { pct: (collectedEvent.confidence * 100)?.toFixed(0) })}
                              sx={{ fontSize: 10, bgcolor: C.neutralBg, color: C.neutral }} />
                            {showEventMetrics && (
                              <Chip size="small"
                                label={t("mktIntelImpactMonths", { v: collectedEvent.effective_months })}
                                sx={{ fontSize: 10, bgcolor: C.tealBg, color: C.teal }} />
                            )}
                            {HIDE_IMPACT_METRICS && (
                              <MetricRevealToggle open={showEventMetrics} onToggle={() => setShowEventMetrics(v => !v)} />
                            )}
                          </Stack>
                          <Typography variant="subtitle2" fontWeight={700} mb={0.3}>
                            {collectedEvent.title}
                          </Typography>
                          {collectedEvent.description && (
                            <Typography variant="body2" color="text.secondary" lineHeight={1.6} fontSize={12.5}>
                              {collectedEvent.description}
                            </Typography>
                          )}
                          {/* AI 요약은 수집 기사를 가공한 결과 — 출처 약관(PDL1.0 등)이 요구하는 가공 표기 */}
                          {attributionKeys(collectedSignals).map(k => (
                            <Typography key={k} variant="caption" color="text.disabled" display="block" mt={0.6}>
                              {t(k)}
                            </Typography>
                          ))}
                          {/* 마케팅 에이전트 실행 정보 병합 — 품질 깔때기 + 판단 기록 (에이전트 경로로 수집된 신호만 보유) */}
                          {collectedEvent._quality && (
                            <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap mt={1}>
                              <Chip size="small" label={t("mktIntelQualityCollected", { n: collectedEvent._quality.signals_raw ?? 0 })}
                                sx={{ height: 18, fontSize: 10, bgcolor: C.neutralBg, color: C.neutral }} />
                              <Chip size="small" label={t("mktIntelQualityCurated", { n: collectedEvent._quality.signals_curated ?? 0 })}
                                sx={{ height: 18, fontSize: 10, bgcolor: C.neutralBg, color: C.neutral }} />
                              <Chip size="small" label={t("mktIntelQualityPassed", { n: collectedEvent._quality.signals_passed ?? 0 })}
                                sx={{ height: 18, fontSize: 10, bgcolor: C.successBg, color: C.success }} />
                              {(collectedEvent._quality.signals_rejected ?? 0) > 0 && (
                                <Chip size="small" label={t("mktIntelQualityRejected", { n: collectedEvent._quality.signals_rejected })}
                                  sx={{ height: 18, fontSize: 10, bgcolor: C.warningBg, color: C.warning }} />
                              )}
                            </Stack>
                          )}
                          {(collectedEvent._decisions?.length > 0) && (
                            <Box mt={0.8}>
                              <Button size="small" onClick={() => setShowAgentDecisions(v => !v)}
                                sx={{ fontSize: 10.5, color: C.neutral, p: "1px 6px", minWidth: 0 }}>
                                {showAgentDecisions ? t("mktIntelDecisionsCollapse") : t("mktIntelDecisionsExpand", { count: collectedEvent._decisions.length })}
                              </Button>
                              <Collapse in={showAgentDecisions}>
                                <AgentDecisionList decisions={collectedEvent._decisions} />
                              </Collapse>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    )}

                    {/* ── 수요 영향 예상 품목 (ai_item_keyword → ai_fact_demand 결합) ── */}
                    {collectedImpact && (
                      <Card variant="outlined" sx={{ borderRadius: 2,
                        border: `1px solid ${C.neutral}25`,
                        bgcolor: "background.paper" }}>
                        <CardContent sx={{ p: "10px 14px !important" }}>
                          <Stack direction="row" alignItems="center" spacing={0.8} mb={1}>
                            <InventoryIcon sx={{ fontSize: 15,
                              color: collectedImpact.fact_ready ? C.success : C.warning }} />
                            <Typography variant="subtitle2" fontWeight={700}
                              color={collectedImpact.fact_ready ? C.success : C.warning}>
                              {t("mktIntelImpactItemsTitle")}
                            </Typography>
                            <Typography variant="caption" color="text.disabled" fontSize={9.5}>
                              {t("mktIntelClickForReason")}
                            </Typography>
                            {collectedImpact.fact_ready ? (
                              <Chip label={t("bfCategoryItemCountLabel", { n: collectedImpact.item_count })} size="small"
                                sx={{ height: 18, fontSize: 9, fontWeight: 700,
                                  bgcolor: C.successBg, color: C.success,
                                  border: `1px solid ${C.success}40` }} />
                            ) : (
                              <Chip label={t("mktIntelNoImpactItems")} size="small"
                                sx={{ height: 18, fontSize: 9, fontWeight: 700,
                                  bgcolor: C.warningBg, color: C.warning,
                                  border: `1px solid ${C.warning}40` }} />
                            )}
                            {collectedImpact.data_source && (
                              <Chip
                                label={{
                                  "dp_cube":       t("mktIntelSrcDpCube"),
                                  "sales_txn":     t("mktIntelSrcSalesTxn"),
                                  "keyword+fact":  t("mktIntelSrcKeywordFact"),
                                  "keyword_only":  t("mktIntelSrcKeywordOnly"),
                                  "master_only":   t("mktIntelSrcMasterOnly"),
                                }[collectedImpact.data_source] || collectedImpact.data_source}
                                size="small"
                                sx={{ ml: "auto !important", height: 18, fontSize: 9,
                                  bgcolor: C.neutralBg, color: C.neutral }} />
                            )}
                          </Stack>

                          {collectedImpact.data_notice && (
                            <Alert severity="warning" icon={false}
                              sx={{ py: 0.3, px: 1, mb: 0.8, borderRadius: 1, fontSize: 11,
                                "& .MuiAlert-message": { lineHeight: 1.5 } }}>
                              {collectedImpact.data_notice}
                            </Alert>
                          )}

                          {collectedImpact.items?.length > 0 ? (
                            <Box sx={{ maxHeight: 200, overflowY: "auto",
                              "&::-webkit-scrollbar": { width: 4 },
                              "&::-webkit-scrollbar-thumb": { bgcolor: `${C.neutral}40`, borderRadius: 2 } }}>
                              <Table size="small" stickyHeader>
                                <TableHead>
                                  {/* sticky 는 셀 단위라 배경색을 TableRow 에 주면 헤더가 비친다 — 불투명색을 TableCell 에만 */}
                                  <TableRow>
                                    {[t("invColItemNm"), t("code"), t("class"), t("mktIntelCol6mSales"), t("mktIntelColInvDays"), t("mktIntelColStatus")].map(h => (
                                      <TableCell key={h}
                                        sx={{ fontWeight: 700, fontSize: 10, color: C.neutral, py: 0.5, bgcolor: C.bg }}>
                                        {h}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {collectedImpact.items.slice(0, 10).map((item, i) => {
                                    const cls = (item.inv_class_cd || "C")[0].toUpperCase();
                                    const stockout = item.stockout_flg === "Y";
                                    const excess   = item.excess_flg   === "Y";
                                    const expanded = expandedImpactIdx === i;
                                    return (
                                      <Fragment key={i}>
                                      <TableRow hover
                                        onClick={() => setExpandedImpactIdx(expanded ? null : i)}
                                        sx={{ cursor: "pointer",
                                              "& td": expanded ? { borderBottom: "none" } : undefined }}>
                                        <TableCell sx={{ py: 0.5 }}>
                                          <Typography variant="body2" fontSize={11.5} fontWeight={600}
                                            lineHeight={1.3} sx={{ maxWidth: 160, overflow: "hidden",
                                              textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {item.item_name}
                                          </Typography>
                                          {item.item_lv1_name && (
                                            <Typography variant="caption" color="text.disabled" fontSize={9.5}
                                              sx={{ display: "block" }}>
                                              {item.item_lv1_name}
                                              {item.item_lv2_name ? ` > ${item.item_lv2_name}` : ""}
                                            </Typography>
                                          )}
                                        </TableCell>
                                        <TableCell sx={{ py: 0.5 }}>
                                          <Typography variant="caption" color="text.secondary" fontSize={10}>
                                            {item.item_cd}
                                          </Typography>
                                        </TableCell>
                                        <TableCell sx={{ py: 0.5 }}>
                                          <Chip label={cls} size="small"
                                            sx={{ fontWeight: 700, fontSize: 10, height: 17,
                                              bgcolor: cls === "A" ? "#dbeafe" : cls === "B" ? "#fef9c3" : C.neutralBg,
                                              color:   cls === "A" ? "#1d4ed8" : cls === "B" ? "#713f12" : C.neutral }} />
                                        </TableCell>
                                        <TableCell sx={{ py: 0.5 }}>
                                          <Typography variant="caption" color="text.secondary" fontSize={10}>
                                            {item.avg_6m_qty != null ? Number(item.avg_6m_qty).toLocaleString() : "-"}
                                          </Typography>
                                        </TableCell>
                                        <TableCell sx={{ py: 0.5 }}>
                                          <Typography variant="caption" color="text.secondary" fontSize={10}>
                                            {item.inv_days != null ? t("mpDayCountLabel", { count: item.inv_days }) : "-"}
                                          </Typography>
                                        </TableCell>
                                        <TableCell sx={{ py: 0.5 }}>
                                          <Stack direction="row" spacing={0.3}>
                                            {stockout && (
                                              <Chip label={t("mktIntelStockoutBadge")} size="small"
                                                sx={{ height: 15, fontSize: 9, fontWeight: 700,
                                                  bgcolor: C.dangerBg, color: C.danger }} />
                                            )}
                                            {excess && (
                                              <Chip label={t("excessBadge")} size="small"
                                                sx={{ height: 15, fontSize: 9, fontWeight: 700,
                                                  bgcolor: C.warningBg, color: C.warning }} />
                                            )}
                                            {!stockout && !excess && (
                                              <Chip label={t("normalBadge")} size="small"
                                                sx={{ height: 15, fontSize: 9,
                                                  bgcolor: C.successBg, color: C.success }} />
                                            )}
                                          </Stack>
                                        </TableCell>
                                      </TableRow>
                                      {/* 선정 근거 — 행 클릭 시 펼침. 백엔드 selection_reason(키워드→필드 매칭),
                                          구 저장분(이전 기록 복원 등)에 없으면 검색 키워드 기반 일반 문구로 폴백. */}
                                      <TableRow>
                                        <TableCell colSpan={6} sx={{ py: 0, borderBottom: expanded ? undefined : "none" }}>
                                          <Collapse in={expanded} timeout="auto" unmountOnExit>
                                            <Box sx={{ my: 0.6, p: "8px 10px", borderRadius: 1,
                                              bgcolor: `${C.primary}08`, border: `1px solid ${C.primary}20` }}>
                                              <Typography variant="caption" fontWeight={700} fontSize={10}
                                                color={C.primary} sx={{ display: "block", mb: 0.3 }}>
                                                {t("mktIntelSelectionReason")}
                                              </Typography>
                                              <Typography variant="caption" fontSize={10.5} color="text.secondary"
                                                sx={{ display: "block", lineHeight: 1.55 }}>
                                                {item.selection_reason
                                                  || t("mktIntelSelectionReasonFallback", { keywords: (collectedImpact.search_keywords || []).join(", ") || "—" })}
                                              </Typography>
                                              {(item._keywords?.length > 0) && (
                                                <Stack direction="row" spacing={0.4} sx={{ mt: 0.5, flexWrap: "wrap", gap: 0.4 }}>
                                                  {item._keywords.slice(0, 8).map((k, ki) => (
                                                    <Chip key={ki} label={k} size="small"
                                                      sx={{ height: 16, fontSize: 9,
                                                        bgcolor: C.neutralBg, color: C.neutral }} />
                                                  ))}
                                                </Stack>
                                              )}
                                            </Box>
                                          </Collapse>
                                        </TableCell>
                                      </TableRow>
                                      </Fragment>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                              {collectedImpact.items.length > 10 && (
                                <Typography variant="caption" color="text.disabled"
                                  sx={{ display: "block", textAlign: "center", mt: 0.5, fontSize: 10 }}>
                                  {t("mktIntelMoreItems", { count: collectedImpact.items.length - 10 })}
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.disabled" fontSize={11.5}>
                              <Trans i18nKey="mktIntelNoFactItems"
                                values={{ keywords: collectedImpact.search_keywords?.join(", ") }}
                                components={{ b: <b /> }} />
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    )}

                    <Card variant="outlined" sx={{ borderRadius: 2, flex: 1 }}>
                      <CardContent sx={{ p: "14px 16px !important" }}>
                        <CommentAndAnalyzeSection
                          userComment={userComment} setUserComment={setUserComment}
                          contextFiles={contextFiles} setContextFiles={setContextFiles}
                          analyzing={analyzing} onAnalyze={handleAnalyze}
                          apiError={apiError} onCloseError={() => setApiError(null)}
                        />
                      </CardContent>
                    </Card>
                  </Stack>
                </Grid>
              </Grid>

              {/* 전체 너비: 분석 로딩 + 결과 */}
              {analyzing && <Box mt={2}><AnalyzingCard /></Box>}
              {result && (
                <Box mt={2}>
                  {/* AI 추천 허브가 권한 밖이면 "보내기" 버튼을 내보내지 않는다(ResultSection 이 핸들러 유무로 판단) */}
                  <ResultSection result={result} resultRef={resultRef} selectedEvent={collectedEvent} isLiveMode={true}
                    onSendToRecommendation={isPermitted("AI_RECOMMENDATION") ? handleSendToRecommendation : undefined} />
                </Box>
              )}
            </>
          )}

      {/* 시장 신호 삭제 확인 — 공통 Dialog */}
      <Dialog
        open={signalDeleteDialog.open}
        title="delete"
        content={t("mktIntelDeleteSignalConfirm")}
        onHandler={handleSignalDeleteConfirm}
        onClose={() => setSignalDeleteDialog({ open: false, signalId: null })}
      />
    </Box>
  );
}

// ── 분석 결과 섹션 (공통) ─────────────────────────────────────────────────
function ResultSection({ result, resultRef, selectedEvent, isLiveMode = false, onSendToRecommendation }) {
  const { t } = useTranslation();
  const [showAllSignals, setShowAllSignals] = useState(false);
  const evtData = result.event || result.steps?.[0]?.output?.event || selectedEvent;

  // Live 모드: 수집된 신호는 이미 좌측 패널에 표시 → 여기서는 key_findings만
  // Mock 모드: result.market_intelligence.signals 표시 (접기 가능)
  const rawSignals   = result.market_intelligence?.signals || [];
  // key_findings: 최상위 또는 market_intelligence.key_insights, 또는 event에서
  const keyFindings  = result.key_findings
    || result.market_intelligence?.key_insights
    || evtData?.key_findings
    || [];
  const isDbAnalysis = result.metadata?.has_signal_impact === true;
  const analysisMode = result.metadata?.analysis_mode || "";
  const isSignalOnly = analysisMode === "signal_only";
  const dataNotice   = result.data_notice || "";
  // 매칭 실적 품목이 없으면 공급망 영향(품목별) 분석을 할 수 없다 — 가상 품목을 지어내지 않고
  // "분석 불가"만 명확히 표시한다(사용자 지시). 신호 자체는 위 '분석 근거 신호'로 확인 가능.
  const noItems = analysisMode === "no_items" || (result.affected_items || []).length === 0;
  const SIGNALS_LIMIT = 3;
  const visibleSignals = showAllSignals ? rawSignals : rawSignals.slice(0, SIGNALS_LIMIT);

  const isErrorFallback = evtData?.source === "error_fallback";

  return (
    <Box ref={resultRef}>
      <Divider sx={{ mb: 2 }}>
        <Chip label={t("mktIntelAiResultDivider")} size="small"
          sx={{ bgcolor: C.primaryBg, color: C.primary, fontWeight: 700, fontSize: 11 }} />
      </Divider>

      {/* ── LLM 응답 오류 시 경고 ── */}
      {isErrorFallback && (
        <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 2, fontSize: 12 }}>
          {t("mktIntelParseErrorNotice")}
        </Alert>
      )}

      {/* ── Mock 모드: 분석에 사용된 신호 (접기 가능) ── */}
      {!isLiveMode && rawSignals.length > 0 && (
        <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
          <CardContent sx={{ p: "12px 14px !important" }}>
            <Stack direction="row" alignItems="center" spacing={0.8} mb={1}>
              <NewspaperIcon sx={{ fontSize: 15, color: C.primary }} />
              <Typography variant="subtitle2" fontWeight={700}>{t("mktIntelEvidenceSignals")}</Typography>
              <Chip label={t("countCases", { count: rawSignals.length })} size="small"
                sx={{ ml: "auto !important", bgcolor: C.primaryBg, color: C.primary,
                  fontWeight: 700, height: 18, fontSize: 10 }} />
              <RiskChip level={result.market_intelligence.risk_level} />
            </Stack>
            {result.market_intelligence._note && (
              <Box sx={{ mb: 1, px: 1, py: 0.5, borderRadius: 1,
                bgcolor: `${C.primary}08`, border: `1px dashed ${C.primary}30` }}>
                <Typography variant="caption" color={C.primary} fontWeight={600}>
                  {result.market_intelligence._note}
                </Typography>
              </Box>
            )}
            {visibleSignals.map((s, i) => <SignalRow key={i} signal={s} />)}
            {rawSignals.length > SIGNALS_LIMIT && (
              <Button size="small" onClick={() => setShowAllSignals(v => !v)}
                sx={{ mt: 0.5, fontSize: 11, color: C.primary, p: "2px 8px" }}>
                {showAllSignals
                  ? t("mktIntelCollapse")
                  : t("mktIntelShowMoreSignals", { count: rawSignals.length - SIGNALS_LIMIT })}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {noItems ? (
        <Card variant="outlined" sx={{ borderRadius: 2, border: `1px solid ${C.warning}40`, bgcolor: C.warningBg }}>
          <CardContent sx={{ p: "16px !important" }}>
            <Stack direction="row" spacing={1.2} alignItems="flex-start">
              <ErrorOutlineIcon sx={{ fontSize: 20, color: C.warning, mt: "1px", flexShrink: 0 }} />
              <Box>
                <Typography variant="subtitle2" fontWeight={700} color={C.warning} mb={0.3}>
                  {t("mktIntelNoMatchTitle")}
                </Typography>
                <Typography variant="body2" color="text.secondary" lineHeight={1.6}>
                  {dataNotice || t("mktIntelNoMatchDesc")}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <DemandInsightCard
          event={evtData}
          intel={result.market_intelligence}
          items={result.affected_items || []}
          dataNotice={dataNotice}
          isSignalOnly={isSignalOnly}
          keyFindings={keyFindings}
          userComment={result.user_comment}
          isDbAnalysis={isDbAnalysis}
        />
      )}

      <Card variant="outlined" sx={{ borderRadius: 2, mt: 2,
        border: `1px solid ${C.success}30`, bgcolor: C.successBg }}>
        <CardContent sx={{ p: "12px 16px !important" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Box>
              <Typography variant="subtitle2" fontWeight={700} color={C.success}>{t("mktIntelNextSteps")}</Typography>
              <Typography variant="body2" color="text.secondary">
                <Trans i18nKey="mktIntelNextStepsDesc" components={{ b: <b /> }} />
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {onSendToRecommendation && (
                <Button variant="contained" size="small"
                  startIcon={<AssignmentTurnedInIcon />}
                  onClick={() => onSendToRecommendation(result)}
                  sx={{ fontWeight: 700, borderRadius: 2, whiteSpace: "nowrap", flexShrink: 0,
                    bgcolor: C.primary, "&:hover": { bgcolor: "#0C447C" } }}>
                  {t("mktIntelGoToReview")}
                </Button>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
